import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { MaintenanceHistoryButton } from "../components/MaintenanceHistory";
import { ToolSetsHistorySection } from "../components/ToolSetsHistorySection";
import { useAuth } from "../context/AuthContext";
import { isDoaga } from "../lib/chf";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { AuthImg, UploadButton } from "../components/AuthImg";
import { DocumentTile } from "../components/DocumentViewer";
import MissingHoursBanner from "../components/MissingHoursBanner";
import { toast } from "sonner";
import { ArrowLeft, Trash2, UserRound, Plus, Wallet, Palette, AlertCircle, UserX, Package, Mail, Download, RefreshCw, ChevronLeft, ChevronRight, ArrowDownToLine, Pencil } from "lucide-react";
import { PasswordInput } from "../components/PasswordInput";
import { missingPersonFields, missingPersonDocs } from "./Personal";
import { useConfirm } from "../components/ConfirmProvider";
import AuditLine from "../components/AuditLine";
import MeldeverfahrenTab from "./MeldeverfahrenTab";
import DamagesPane from "../components/DamagesPane";
import PSAAssignmentPanel from "../components/PSAAssignmentPanel";
import { ProjectSearchSelect } from "../components/ProjectSearchSelect";
import { projectActiveOnDay, regieActiveOnDay } from "../lib/activeOnDay";
import PSAPane from "../components/PSAPane";
import TagesraportPanel from "../components/TagesraportPanel";
import EquipmentTimelinePane from "../components/EquipmentTimelinePane";
import DrivingHistoryPane from "./persondetail/DrivingHistoryPane";
import MonthlyReportsPane from "./persondetail/MonthlyReportsPane";
import { AssignedItemsDialog, DismissalDialog, RehireDialog } from "./persondetail/EquipmentDialogs";
import { fmtRoDate } from "../lib/dateFmt";
import { PersonVacationsTab } from "../components/PersonVacationsTab";
import { PersonMonthWorkLog } from "../components/PersonMonthWorkLog";
import { MonthPicker } from "../components/MonthPicker";

const MONTH_NAMES = ["Ianuarie","Februarie","Martie","Aprilie","Mai","Iunie","Iulie","August","Septembrie","Octombrie","Noiembrie","Decembrie"];

// Paletă fixă culori pentru bulinele de proiect din calendar
export const BULLET_PALETTE = [
  { hex: "#0044FF", label: "Albastru" },
  { hex: "#16A34A", label: "Verde" },
  { hex: "#F59E0B", label: "Portocaliu" },
  { hex: "#DC2626", label: "Roșu" },
  { hex: "#7C3AED", label: "Mov" },
  { hex: "#0EA5E9", label: "Cyan" },
  { hex: "#EC4899", label: "Roz" },
  { hex: "#475569", label: "Gri" },
];

export default function PersonDetail() {
  const confirm = useConfirm();
  const { id } = useParams();
  const { canDo, isAdmin, user: authUser } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [rentals, setRentals] = useState([]);
  const [employerCompanies, setEmployerCompanies] = useState([]);
  const [saveStatus, setSaveStatus] = useState("idle");
  const saveTimer = useRef(null);
  const skipNextAutoSave = useRef(true);
  const lastLoadedJson = useRef("");
  const [tabValue, setTabValue] = useState(null);
  const [dismissalOpen, setDismissalOpen] = useState(false);
  const [assignedOpen, setAssignedOpen] = useState(false);
  const [rehireOpen, setRehireOpen] = useState(false);
  const [siblingIds, setSiblingIds] = useState([]); // ordered list for prev/next navigation
  const [auditRow, setAuditRow] = useState(null); // /admin/audit-coverage row for this person

  const loadAudit = async () => {
    try {
      const { data } = await api.get("/admin/audit-coverage");
      const row = (data?.rows || []).find((r) => r.person_id === id);
      setAuditRow(row || null);
    } catch { setAuditRow(null); }
  };

  const load = async () => {
    const [{ data }, r, ec, allP] = await Promise.all([
      api.get(`/persons/${id}`),
      api.get("/rentals").catch(() => ({ data: [] })),
      api.get("/employer-companies").catch(() => ({ data: [] })),
      api.get("/persons-overview").catch(() => ({ data: [] })),
    ]);
    skipNextAutoSave.current = true;
    setP(data);
    lastLoadedJson.current = JSON.stringify(data);
    setRentals(r.data || []);
    setEmployerCompanies(ec.data || []);
    setSiblingIds((allP.data || []).map((x) => x.id));
    loadAudit();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setField = (k, v) => setP((x) => ({ ...x, [k]: v }));

  // Prev/Next navigation helpers — keep current hash (#tab) so user lands on same tab
  const currentIdx = siblingIds.indexOf(id);
  const prevId = currentIdx > 0 ? siblingIds[currentIdx - 1] : null;
  const nextId = currentIdx >= 0 && currentIdx < siblingIds.length - 1 ? siblingIds[currentIdx + 1] : null;
  const goTo = (pid) => {
    if (!pid) return;
    const hash = (typeof window !== "undefined" ? window.location.hash : "") || "";
    navigate(`/personal/${pid}${hash}`);
  };
  // Keyboard shortcuts: ArrowLeft / ArrowRight when not inside an input
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const editable = e.target?.isContentEditable;
      if (tag === "input" || tag === "textarea" || tag === "select" || editable) return;
      if (e.altKey || e.metaKey || e.ctrlKey) return;
      if (e.key === "ArrowLeft" && prevId) { e.preventDefault(); goTo(prevId); }
      else if (e.key === "ArrowRight" && nextId) { e.preventDefault(); goTo(nextId); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    /* eslint-disable-next-line */
  }, [prevId, nextId]);

  // Auto-save: debounced 1s after any change to `p`
  useEffect(() => {
    if (!p) return;
    if (skipNextAutoSave.current) {
      skipNextAutoSave.current = false;
      return;
    }
    const currentJson = JSON.stringify(p);
    if (currentJson === lastLoadedJson.current) return;
    if (!canDo("PERSONAL", "edit")) return;
    setSaveStatus("dirty");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        const { data } = await api.put(`/persons/${id}`, p);
        lastLoadedJson.current = JSON.stringify(data);
        skipNextAutoSave.current = true;
        setP(data);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus((s) => (s === "saved" ? "idle" : s)), 2000);
      } catch {
        setSaveStatus("error");
      }
    }, 1000);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line
  }, [p]);

  const remove = async () => {
    if (!(await confirm({ body: "Ștergi persoana și datele asociate?", danger: true }))) return;
    await api.delete(`/persons/${id}`);
    toast.success("Șters");
    navigate("/personal");
  };

  if (!p) return <div className="text-zinc-500">Se încarcă...</div>;

  const canEdit = canDo("PERSONAL", "edit");

  // Tab visibility: admin sees all; non-admin respects user.personal_tab_perms.
  // Empty/undefined map → all visible (backward compat). Otherwise only true keys.
  const tabPerms = authUser?.personal_tab_perms || {};
  const tabPermsExplicit = !isAdmin && Object.values(tabPerms).some((v) => v === true);
  const showTab = (key) => isAdmin || !tabPermsExplicit || !!tabPerms[key];
  const showDate = showTab("date");
  const showCazare = showTab("cazare");
  const showDocumente = showTab("documente");
  const showCalendar = showTab("calendar") && !isDoaga(authUser);
  // Eligibilitate Tagesraport — Khreis Christian, Istratenco Serghei, Stoica Elena Lorena
  const isTagesraportEligible = (() => {
    if (!p) return false;
    const nume = (p.nume || "").trim().toLowerCase();
    const prenume = (p.prenume || "").trim().toLowerCase();
    const prenumeTokens = new Set(prenume.split(/\s+/).filter(Boolean));
    const ELIGIBLE = [["khreis", "christian"], ["istratenco", "serghei"], ["stoica", "lorena"]];
    return ELIGIBLE.some(([en, ep]) =>
      (nume === en && (prenume === ep || prenumeTokens.has(ep))) ||
      (nume === ep && (prenume === en || prenumeTokens.has(en)))
    );
  })();
  const showTagesraport = isTagesraportEligible && (isAdmin || authUser?.person_id === id);
  // Honor URL hash like #calendar / #cheltuieli to deep-link from Dashboard quick actions
  const hashTab = (typeof window !== "undefined" ? window.location.hash : "").replace("#", "");
  const VALID_HASH_TABS = new Set(["date", "cazare", "documente", "concedii", "calendar", "cheltuieli", "cont", "meldeverfahren", "daune", "psa"]);
  const initialFromHash = VALID_HASH_TABS.has(hashTab) ? hashTab : null;
  const fallbackTab = showDate
    ? "date"
    : showCazare
    ? "cazare"
    : showDocumente
    ? "documente"
    : showCalendar
    ? "calendar"
    : (isAdmin || authUser?.person_id === id)
    ? "cheltuieli"
    : "date";
  const defaultTab = initialFromHash || fallbackTab;

  return (
    <div data-testid="page-person-detail">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900" data-testid="back-btn">
            <ArrowLeft size={16} /> Înapoi
          </button>
          <div className="h-5 w-px bg-zinc-200" />
          <div className="inline-flex items-center gap-1" data-testid="person-nav">
            <Button
              size="sm"
              variant="outline"
              disabled={!prevId}
              onClick={() => goTo(prevId)}
              className="h-8 px-2 disabled:opacity-40"
              title={prevId ? "Persoana anterioară (← săgeată stânga)" : "Ești la primul angajat"}
              data-testid="person-nav-prev"
            >
              <ChevronLeft size={16} />
              <span className="hidden md:inline ml-1 text-xs">Anterior</span>
            </Button>
            {currentIdx >= 0 && siblingIds.length > 0 && (
              <span className="text-[11px] text-zinc-500 px-2 font-mono" data-testid="person-nav-position">
                {currentIdx + 1} / {siblingIds.length}
              </span>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={!nextId}
              onClick={() => goTo(nextId)}
              className="h-8 px-2 disabled:opacity-40"
              title={nextId ? "Persoana următoare (→ săgeată dreapta)" : "Ești la ultimul angajat"}
              data-testid="person-nav-next"
            >
              <span className="hidden md:inline mr-1 text-xs">Următor</span>
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium" data-testid="save-status">
              {saveStatus === "idle" && <span className="text-zinc-400">Salvare automată activă</span>}
              {saveStatus === "dirty" && <span className="text-amber-600">● Modificări nesalvate…</span>}
              {saveStatus === "saving" && <span className="text-[var(--primary)]">⟳ Se salvează…</span>}
              {saveStatus === "saved" && <span className="text-[var(--green-valid)]">✓ Salvat</span>}
              {saveStatus === "error" && <span className="text-red-600">⚠ Eroare salvare</span>}
            </span>
          )}
          <Button variant="outline" data-testid="assigned-items-btn" onClick={() => setAssignedOpen(true)} className="border-blue-300 text-blue-700 hover:bg-blue-50"><Package size={16} className="mr-1"/>Echipament alocat</Button>
          {isAdmin && p.active !== false && !p.data_concedierii && <Button variant="outline" data-testid="dismissal-btn" onClick={() => setDismissalOpen(true)} className="border-red-300 text-red-700 hover:bg-red-50"><UserX size={16} className="mr-1"/>Concediază</Button>}
          {isAdmin && <Button variant="destructive" data-testid="delete-person" onClick={remove}><Trash2 size={16} className="mr-1" />Șterge</Button>}
        </div>
      </div>

      {(p.hourly_rate == null || Number(p.hourly_rate) <= 0) && p.active !== false && (
        <div
          className="mb-4 p-3 rounded-md border-2 border-amber-400 bg-amber-50 flex items-start gap-3"
          data-testid="missing-hourly-rate-banner"
        >
          <span className="text-2xl leading-none">⚠</span>
          <div className="flex-1">
            <div className="font-bold text-amber-900 text-sm">Tarif orar nesetat</div>
            <div className="text-xs text-amber-800 mt-0.5">
              Persoana nu are tarif orar (CHF/h). Orele logate vor avea <strong>Salariu = 0 CHF</strong> (doar diurna).
              Setează tariful mai jos la „💰 Tarif orar (CHF/h)".
            </div>
          </div>
        </div>
      )}

      <div className="card-flat p-6 mb-6 flex items-start gap-6">
        <div className="flex flex-col items-center gap-2">
          {p.photo_file_id ? (
            <AuthImg fileId={p.photo_file_id} className="w-24 h-24 rounded-full object-cover" />
          ) : (
            <div className="w-24 h-24 rounded-full bg-zinc-100 grid place-items-center text-zinc-400"><UserRound size={32}/></div>
          )}
          {canEdit && (
            <UploadButton
              testId="upload-person-photo"
              label="Schimbă foto"
              onUploaded={(f) => { setField("photo_file_id", f.id); api.put(`/persons/${id}`, { photo_file_id: f.id }).then(load); }}
            />
          )}
        </div>
        <div className="flex-1">
          <h1 className="font-display text-3xl font-extrabold">{p.nume || "—"} {p.prenume || ""}</h1>
          <div className="flex items-center gap-3 mt-2 text-sm text-zinc-500 flex-wrap">
            <span>{p.telefon || "Fără telefon"}</span>
            <span>·</span>
            <span>Angajat: {fmtRoDate(p.data_angajarii) || "—"}</span>
            <MaintenanceHistoryButton personId={id} label="ISTORIC" />
            {(() => {
              const f = employerCompanies.find((c) => c.id === p.firma_id);
              if (!f) return null;
              return (<>
                <span>·</span>
                <span data-testid="person-detail-firma">Firma: <span className="text-zinc-800 font-medium">{f.name}</span></span>
              </>);
            })()}
          </div>
          <ToolSetsHistorySection person_id={id} title="🧰 Scule folosite (istoric)" compact />
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <div className={`inline-flex items-center gap-3 px-3 py-1.5 rounded-md border-2 ${p.active === false ? "border-red-500 bg-red-50" : "border-emerald-500 bg-emerald-50"}`} data-testid="active-toggle-wrapper">
              <Label className={`small-label m-0 ${p.active === false ? "text-red-600 font-bold" : "text-emerald-700 font-semibold"}`}>
                {p.active === false ? "INACTIV" : "ACTIV"}
              </Label>
              <Switch data-testid="toggle-active" checked={p.active !== false} onCheckedChange={(v) => setField("active", v)} disabled={!canEdit} />
            </div>
            {canEdit && p.active !== false && !p.data_concedierii && (
              <button
                type="button"
                onClick={() => setDismissalOpen(true)}
                className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider bg-red-600 text-white hover:bg-red-700 transition-colors"
                data-testid="fire-person-now"
                title="Deschide dialogul de concediere (alegi ultima zi de muncă, echipamente de predat etc.)"
              >
                🚫 Concediază
              </button>
            )}
            {p.data_concedierii && (
              <>
                <span className="ml-2 text-[11px] uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200" title={`Concediat pe ${p.data_concedierii}`}>
                  Concediat · {p.data_concedierii}
                </span>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setRehireOpen(true)}
                    className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider bg-green-600 text-white hover:bg-green-700 transition-colors"
                    data-testid="rehire-btn"
                    title="Reangajează această persoană (alegi data de la care reîncepe activitatea)"
                  >
                    🔄 Reangajează
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* === BIG ERRORS BANNER — sumar erori cu acțiuni rapide === */}
      {(() => {
        const missing = missingPersonFields(p);
        const missingDocs = missingPersonDocs(p, employerCompanies);
        const missingAudit = (auditRow?.missing_months || []).length;
        const missingSlips = (auditRow?.missing_slips || []).length;
        const slipMismatches = (auditRow?.slip_mismatches || []).length;
        const noPsa = auditRow && auditRow.psa_count === 0 && p.active !== false ? 1 : 0;
        const total = missing.length + missingDocs.length + missingAudit + missingSlips + slipMismatches + noPsa;
        if (total === 0) return null;
        const hasCritical = missingDocs.length > 0 || missing.length >= 3 || missingAudit >= 3 || missingSlips > 0 || slipMismatches > 0 || noPsa > 0;
        const goTo = (tab) => {
          setTabValue(tab);
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="tab-${tab}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 50);
        };
        const breakdown = [
          { count: missing.length, label: "date personale", color: "amber", tab: "date" },
          { count: missingDocs.length, label: "documente", color: "red", tab: "documente" },
          { count: missingAudit, label: missingAudit === 1 ? "raport lunar" : "rapoarte lunare", color: "red", tab: "monthly-reports" },
          { count: missingSlips, label: missingSlips === 1 ? "fișă salariu" : "fișe salariu", color: "red", tab: "monthly-reports" },
          { count: slipMismatches, label: missingSlips === 1 ? "discrepanță" : "discrepanțe", color: "red", tab: "monthly-reports" },
          { count: noPsa, label: "PSA nealocat", color: "red", tab: "psa" },
        ].filter((b) => b.count > 0);
        const wrap = hasCritical
          ? "alert-banner-red border-2 border-red-500 text-red-900 ring-2 ring-red-300 shadow-xl shadow-red-200/50 animate-pulse-red-soft"
          : "alert-banner-orange border-2 border-orange-400 text-orange-900 shadow-md";
        const colorChip = (c) => ({
          red: "bg-red-100 text-red-800 border-red-300 hover:bg-red-200",
          amber: "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200",
        }[c]);
        return (
          <div
            className={`card-flat p-5 mb-4 rounded-xl flex items-stretch gap-4 ${wrap}`}
            data-testid="person-errors-summary-banner"
          >
            <div className="flex flex-col items-center justify-center px-4 py-3 rounded-lg bg-white/50 border-2 border-current shrink-0 min-w-[110px]">
              <AlertCircle size={32} className={hasCritical ? "text-red-700 animate-pulse" : "text-orange-700"}/>
              <div className="text-4xl font-extrabold leading-none mt-1 tabular-nums">{total}</div>
              <div className="text-[10px] font-extrabold uppercase tracking-wider mt-0.5 text-center">
                erori<br/>de rezolvat
              </div>
            </div>
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <div className="text-base font-extrabold uppercase tracking-wider mb-1">
                {hasCritical ? "🚨 ACȚIUNE URGENTĂ NECESARĂ" : "⚠ Date incomplete · Te rugăm să rezolvi"}
              </div>
              <div className="text-xs opacity-90 mb-2">
                Profilul lui <strong>{p.nume} {p.prenume}</strong> are <strong>{total}</strong> {total === 1 ? "problemă" : "probleme"} care necesită{total === 1 ? "" : ""} atenția ta. Click pe categoriile de mai jos pentru a sări la secțiunea respectivă.
              </div>
              <div className="flex flex-wrap gap-1.5">
                {breakdown.map((b) => (
                  <button
                    key={b.label + b.tab}
                    type="button"
                    onClick={() => goTo(b.tab)}
                    className={`text-xs px-2.5 py-1 rounded-full border-2 font-bold transition-all hover:scale-105 ${colorChip(b.color)}`}
                    data-testid={`person-errors-chip-${b.tab}`}
                    title={`Click pentru a vedea ${b.label}`}
                  >
                    {b.count} {b.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {(() => {
        const missing = missingPersonFields(p);
        const missingDocs = missingPersonDocs(p, employerCompanies);
        if (missing.length === 0 && missingDocs.length === 0) return null;
        const goTo = (tab) => {
          setTabValue(tab);
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="tab-${tab}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 50);
        };
        // Documente critice = act identitate, contract de muncă (ConVic), Kündigung (concediat)
        const hasCritical = missingDocs.some((m) => m.key === "buletin" || m.key === "contract_munca" || m.key === "kundigung");
        const tone = hasCritical
          ? { wrap: "bg-red-50 border-red-300 ring-2 ring-red-200", icon: "text-red-600", btn: "bg-red-600 hover:bg-red-700", title: "text-red-900", chip: "bg-red-100 text-red-800 border-red-200", divider: "border-red-200" }
          : { wrap: "bg-amber-50 border-amber-200", icon: "text-amber-600", btn: "bg-amber-600 hover:bg-amber-700", title: "text-amber-900", chip: "bg-amber-100 text-amber-800 border-amber-200", divider: "border-amber-200" };
        return (
          <div
            className={`card-flat p-4 mb-4 ${tone.wrap} space-y-3`}
            data-testid="person-missing-banner"
          >
            {missing.length > 0 && (
              <div className="flex items-start gap-3">
                <AlertCircle size={22} className={`shrink-0 ${tone.icon} mt-0.5`}/>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-bold ${tone.title}`}>
                    Date lipsă · Te rugăm să completezi {missing.length === 1 ? "câmpul" : `cele ${missing.length} câmpuri`} de mai jos
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {missing.map((m) => (
                      <span
                        key={m.key}
                        className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${tone.chip} font-semibold border`}
                        data-testid={`person-missing-${m.key}`}
                      >
                        {m.label}
                      </span>
                    ))}
                  </div>
                </div>
                {canEdit && showDate && (
                  <Button
                    size="sm"
                    onClick={() => goTo("date")}
                    className={`${tone.btn} text-white shrink-0`}
                    data-testid="person-missing-fill-btn"
                  >
                    Completează
                  </Button>
                )}
              </div>
            )}
            {missingDocs.length > 0 && (
              <div className={`flex items-start gap-3 ${missing.length > 0 ? `pt-3 border-t ${tone.divider}` : ""}`}>
                <AlertCircle size={22} className={`shrink-0 ${tone.icon} mt-0.5 ${hasCritical ? "animate-pulse" : ""}`}/>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-bold ${tone.title}`}>
                    {hasCritical
                      ? `⚠ Documente obligatorii lipsă · Încarcă-le acum`
                      : missingDocs.length === 1
                        ? `${missingDocs[0].label} lipsește · Încarcă-l acum`
                        : `${missingDocs.length} documente obligatorii lipsesc · Încarcă-le acum`}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {missingDocs.map((m) => (
                      <span
                        key={m.key}
                        className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${tone.chip} font-semibold border`}
                        data-testid={`person-missing-doc-${m.key}`}
                      >
                        📎 {m.label}
                      </span>
                    ))}
                  </div>
                </div>
                {canEdit && showDocumente && (
                  <Button
                    size="sm"
                    onClick={() => goTo("documente")}
                    className={`${tone.btn} text-white shrink-0`}
                    data-testid="person-missing-docs-btn"
                  >
                    Încarcă
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })()}

      <Tabs value={tabValue || defaultTab} onValueChange={setTabValue}>
        {(() => {
          // Pre-calculate counts pentru highlighting tab-uri cu erori
          const _missing = missingPersonFields(p);
          const _missingDocs = missingPersonDocs(p, employerCompanies);
          const _missingAudit = (auditRow?.missing_months || []).length;
          const _missingSlips = (auditRow?.missing_slips || []).length;
          const _slipMismatches = (auditRow?.slip_mismatches || []).length;
          const _noPsa = auditRow && auditRow.psa_count === 0 && p.active !== false;
          const _monthlyTotal = _missingAudit + _missingSlips + _slipMismatches;
          const errCls = "bg-red-100 text-red-900 ring-2 ring-red-400 animate-pulse-red-soft data-[state=active]:bg-red-200 data-[state=active]:text-red-900";
          const ErrBadge = ({ n }) => (
            <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[9px] font-extrabold tabular-nums">
              {n}
            </span>
          );
          return (
            <TabsList className="flex-wrap h-auto">
              {showDate && (
                <TabsTrigger
                  value="date"
                  data-testid="tab-date"
                  className={_missing.length > 0 ? errCls : ""}
                >
                  Date{_missing.length > 0 && <ErrBadge n={_missing.length}/>}
                </TabsTrigger>
              )}
              {showCazare && <TabsTrigger value="cazare" data-testid="tab-cazare">Cazare</TabsTrigger>}
              {showDocumente && (
                <TabsTrigger
                  value="documente"
                  data-testid="tab-documente"
                  className={_missingDocs.length > 0 ? errCls : ""}
                >
                  Documente{_missingDocs.length > 0 && <ErrBadge n={_missingDocs.length}/>}
                </TabsTrigger>
              )}
              <TabsTrigger value="concedii" data-testid="tab-concedii">🏖 Concedii / Absențe</TabsTrigger>
              {showCalendar && <TabsTrigger value="calendar" data-testid="tab-calendar">Calendar Ore</TabsTrigger>}
              <TabsTrigger value="plan" data-testid="tab-plan">📅 Plan</TabsTrigger>
              {p.permis_conducere && <TabsTrigger value="driving" data-testid="tab-driving">🚗 Mașini conduse</TabsTrigger>}
              {p.is_subcontractor && <TabsTrigger value="meldeverfahren" data-testid="tab-meldeverfahren">🌍 Meldeverfahren</TabsTrigger>}
              <TabsTrigger value="daune" data-testid="tab-daune">⚠ Daune</TabsTrigger>
              <TabsTrigger
                value="psa"
                data-testid="tab-psa"
                className={_noPsa ? errCls : ""}
              >
                🦺 PSA Alocat{_noPsa && <ErrBadge n={0}/>}
              </TabsTrigger>
              <TabsTrigger value="cronologie" data-testid="tab-cronologie">📜 Cronologie</TabsTrigger>
              {showTagesraport && <TabsTrigger value="tagesraport" data-testid="tab-tagesraport">📝 Tagesraport</TabsTrigger>}
              {isAdmin && !isDoaga(authUser) && (
                <TabsTrigger
                  value="monthly-reports"
                  data-testid="tab-monthly-reports"
                  className={_monthlyTotal > 0 ? errCls : ""}
                >
                  📋 Rapoarte lunare{_monthlyTotal > 0 && <ErrBadge n={_monthlyTotal}/>}
                </TabsTrigger>
              )}
              {(isAdmin || authUser?.person_id === id) && <TabsTrigger value="cheltuieli" data-testid="tab-cheltuieli">Cheltuielile mele</TabsTrigger>}
              {isAdmin && <TabsTrigger value="cont" data-testid="tab-cont">Cont utilizator</TabsTrigger>}
            </TabsList>
          );
        })()}

        <TabsContent value="date">
          <div className="card-flat p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Nume" v={p.nume} onChange={(v) => setField("nume", v)} disabled={!canEdit} />
            <F label="Prenume" v={p.prenume} onChange={(v) => setField("prenume", v)} disabled={!canEdit} />
            <F label="Data nașterii" type="date" v={p.data_nasterii} onChange={(v) => setField("data_nasterii", v)} disabled={!canEdit} />
            <F label="Telefon" v={p.telefon} onChange={(v) => setField("telefon", v)} disabled={!canEdit} />
            <F label="Adresă" v={p.adresa} onChange={(v) => setField("adresa", v)} disabled={!canEdit} className="md:col-span-2" />
            <F label="Data angajării" type="date" v={p.data_angajarii} onChange={(v) => setField("data_angajarii", v)} disabled={!canEdit} />
            <div className="md:col-span-2">
              <Label className="small-label">Firma angajatoare</Label>
              <Select
                value={p.firma_id || "__none__"}
                onValueChange={(v) => {
                  const newFid = v === "__none__" ? null : v;
                  const firm = employerCompanies.find((c) => c.id === newFid);
                  const isConvic = !firm || /^convic/i.test(firm.name || "");
                  // Update both fields atomically so auto-save sends both
                  setP((prev) => ({ ...prev, firma_id: newFid, is_subcontractor: !isConvic }));
                }}
                disabled={!canEdit}
              >
                <SelectTrigger className="mt-1" data-testid="person-firma-select"><SelectValue placeholder="Selectează firma" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Niciuna —</SelectItem>
                  {employerCompanies.filter((c) => c.active !== false).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-500 mt-1">Pentru orice firmă diferită de „ConVic", se activează automat tracking-ul Meldeverfahren.</p>
            </div>
            <div className="flex items-center gap-3 py-2">
              <Label className="small-label">Permis Conducere</Label>
              <Switch checked={!!p.permis_conducere} onCheckedChange={(v) => setField("permis_conducere", v)} disabled={!canEdit} />
              <span className="text-sm">{p.permis_conducere ? "DA" : "NU"}</span>
            </div>
            <div className="flex items-center gap-3 py-2">
              <Label className="small-label">EKAS</Label>
              <Switch checked={!!p.ekas} onCheckedChange={(v) => setField("ekas", v)} disabled={!canEdit} data-testid="detail-ekas-toggle" />
              <span className="text-sm inline-flex items-center gap-1">{p.ekas ? <><span className="text-yellow-500">⭐</span> DA</> : "NU"}</span>
            </div>
            <div className="flex items-center gap-3 py-2">
              <Label className="small-label">Limba germană</Label>
              <Switch checked={!!p.limba_germana} onCheckedChange={(v) => setField("limba_germana", v)} disabled={!canEdit} data-testid="detail-limba-germana-toggle" />
              <span className="text-sm inline-flex items-center gap-1">{p.limba_germana ? <><span>🇩🇪</span> DA</> : "NU"}</span>
            </div>
            <div className="flex items-center gap-3 py-2" title="Angajat al unui subcontractor — supus regulii Meldeverfahren (max 90 zile lucrătoare/an în CH)">
              <Label className="small-label">Subcontractor (Meldeverfahren)</Label>
              <Switch checked={!!p.is_subcontractor} onCheckedChange={(v) => setField("is_subcontractor", v)} disabled={!canEdit} data-testid="detail-subcontractor-toggle" />
              <span className="text-sm inline-flex items-center gap-1">{p.is_subcontractor ? <><span>🌍</span> DA</> : "NU"}</span>
            </div>
            <div className="md:col-span-2 pt-3 border-t border-[var(--border)] text-xs text-zinc-500 italic" data-testid="person-detail-psa-link-hint">
              🦺 Echipamentul PSA al acestei persoane este în tab-ul dedicat <strong>„🦺 PSA Alocat"</strong> (vezi tab-uri sus).
            </div>
            <F label="Alte Detalii" v={p.alte_detalii} onChange={(v) => setField("alte_detalii", v)} disabled={!canEdit} textarea className="md:col-span-2" />

            <div className="md:col-span-2 pt-3 border-t border-[var(--border)] grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="small-label">💰 Tarif orar (CHF/h)</Label>
                <Input
                  type="number" step={0.5} min={0}
                  className={`mt-1 max-w-[240px] ${(p.hourly_rate == null || Number(p.hourly_rate) <= 0) ? "border-amber-400 ring-1 ring-amber-300" : ""}`}
                  value={p.hourly_rate ?? ""}
                  onChange={(e) => setField("hourly_rate", e.target.value === "" ? null : (parseFloat(e.target.value) || 0))}
                  disabled={!canEdit}
                  placeholder="ex: 25"
                  data-testid="person-hourly-rate"
                />
                <p className={`text-[11px] mt-1 ${(p.hourly_rate == null || Number(p.hourly_rate) <= 0) ? "text-amber-700 font-medium" : "text-zinc-500"}`}>
                  {(p.hourly_rate == null || Number(p.hourly_rate) <= 0)
                    ? "⚠ Setează tariful — fără el, calculul lunar va arăta Salariu = 0 CHF."
                    : "Va fi auto-completat în Calendar Ore — nu mai trebuie să-l introduci la fiecare zi."}
                </p>
              </div>
              <div>
                <Label className="small-label">📅 Salariu lunar (CHF)</Label>
                <Input
                  type="number" step={50} min={0}
                  className="mt-1 max-w-[240px]"
                  value={p.monthly_salary ?? ""}
                  onChange={(e) => setField("monthly_salary", e.target.value === "" ? null : (parseFloat(e.target.value) || 0))}
                  disabled={!canEdit}
                  placeholder="ex: 4500"
                  data-testid="person-monthly-salary"
                />
                <p className="text-[11px] text-zinc-500 mt-1">Opțional — informativ. Completează doar dacă persoana e plătită cu salariu fix lunar.</p>
              </div>
            </div>

            <div className="md:col-span-2 pt-3 border-t border-[var(--border)]">
              <Label className="small-label">👶 Număr copii</Label>
              <Input
                type="number" min={0} step={1}
                className="mt-1 max-w-[200px]"
                value={p.nr_copii ?? ""}
                onChange={(e) => setField("nr_copii", e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value || "0")))}
                disabled={!canEdit}
                placeholder="0"
                data-testid="person-nr-copii"
              />
            </div>

            <div className="md:col-span-2 space-y-3">
              <Label className="small-label">Bilă Roșie (până la 3 intrări)</Label>
              {[0, 1, 2].map((i) => {
                const list = Array.isArray(p.bila_rosie) ? p.bila_rosie : (p.bila_rosie ? [p.bila_rosie] : []);
                const val = list[i] || "";
                const filled = val && val.trim().length > 0;
                return (
                  <div key={i} className={`p-4 rounded-md border ${filled ? "rubric-red" : "border-[var(--border)] bg-white"}`}>
                    <div className="flex items-center gap-2">
                      <span className={`bila-dot ${filled ? "bila-red bila-pulse" : "bila-green"}`} />
                      <Label className="small-label">Bilă Roșie #{i + 1}</Label>
                    </div>
                    <Textarea
                      data-testid={`bila-rosie-input-${i}`}
                      className="mt-2 bg-white"
                      rows={2}
                      value={val}
                      onChange={(e) => {
                        const curr = Array.isArray(p.bila_rosie) ? [...p.bila_rosie] : (p.bila_rosie ? [p.bila_rosie] : []);
                        while (curr.length < 3) curr.push("");
                        curr[i] = e.target.value;
                        setField("bila_rosie", curr);
                      }}
                      disabled={!canEdit}
                      placeholder={`Detalii bila roșie ${i + 1}...`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="cazare">
          <div className="card-flat p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="small-label">Tip cazare</Label>
              <Select
                value={p.cazare_type || (p.rental_id ? "rental" : (p.adresa_cazare ? "other" : "own"))}
                onValueChange={(v) => {
                  if (v === "own") setP((x) => ({ ...x, cazare_type: "own", rental_id: null, adresa_cazare: "" }));
                  else if (v === "other") setP((x) => ({ ...x, cazare_type: "other", rental_id: null }));
                  else setP((x) => ({ ...x, cazare_type: "rental" }));
                }}
                disabled={!canEdit}
              >
                <SelectTrigger className="mt-2" data-testid="cazare-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rental">Cazare din Chirii</SelectItem>
                  <SelectItem value="other">Altă adresă</SelectItem>
                  <SelectItem value="own">Adresă proprie</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(p.cazare_type === "rental" || (!p.cazare_type && p.rental_id)) && (
              <div className="md:col-span-2">
                <Label className="small-label">Selectează cazarea din Chirii</Label>
                <Select
                  value={p.rental_id || ""}
                  onValueChange={(v) => {
                    const r = rentals.find((x) => x.id === v);
                    setP((x) => ({ ...x, rental_id: v, adresa_cazare: r ? r.adresa : "", cazare_type: "rental" }));
                  }}
                  disabled={!canEdit}
                >
                  <SelectTrigger className="mt-2" data-testid="cazare-rental-select">
                    <SelectValue placeholder={rentals.length === 0 ? "Niciun contract — adaugă din modulul Chirii" : "Selectează adresă"} />
                  </SelectTrigger>
                  <SelectContent>
                    {rentals.map((r) => {
                      const cap = r.capacitate_persoane || 0;
                      const label = r.titlu ? `${r.titlu} — ${r.adresa}` : r.adresa;
                      return <SelectItem key={r.id} value={r.id}>{label} · {r.nr_camere || 0} cam{cap ? ` · cap. ${cap}` : ""}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
                {p.rental_id && (() => {
                  const r = rentals.find((x) => x.id === p.rental_id);
                  if (!r) return null;
                  return (
                    <div className="mt-3 p-3 rounded-md bg-zinc-50 border border-[var(--border)] text-sm">
                      <div className="font-semibold">{r.titlu || r.adresa}</div>
                      {r.titlu && <div className="text-xs text-zinc-500 mt-0.5">{r.adresa}</div>}
                      <div className="text-xs text-zinc-500 mt-1">
                        {r.nr_camere || 0} camere · capacitate {r.capacitate_persoane || "—"} · {r.pret_lunar || 0} CHF/lună · de la {r.de_la_cine || "—"}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {p.cazare_type === "other" && (
              <div className="md:col-span-2">
                <F label="Adresa Cazare / Cameră" v={p.adresa_cazare} onChange={(v) => setField("adresa_cazare", v)} disabled={!canEdit} />
              </div>
            )}

            {p.cazare_type === "own" && (
              <div className="md:col-span-2 text-sm text-zinc-500 italic">
                Persoana folosește adresa proprie: <span className="text-zinc-900 not-italic font-medium">{p.adresa || "—"}</span>
              </div>
            )}

            <div>
              <Label className="small-label">Chei Cazare (0-4)</Label>
              <Input type="number" min={0} max={4} placeholder="0" value={p.chei_cazare ?? ""} onChange={(e) => setField("chei_cazare", e.target.value === "" ? "" : Math.max(0, Math.min(4, parseInt(e.target.value || "0"))))} disabled={!canEdit} className="mt-2" />
            </div>
          </div>

          <PersonAssignedKeysCard personId={id} />
        </TabsContent>

        <TabsContent value="documente">
          <Documente person={p} onChanged={load} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="concedii">
          <PersonVacationsTab personId={id} personName={`${p.nume || ""} ${p.prenume || ""}`.trim()} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="calendar">
          <CalendarPane personId={id} canEdit={canEdit} defaultRate={p.hourly_rate || 0} personName={p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim()} />
        </TabsContent>

        <TabsContent value="plan">
          <PersonPlanTab personId={id} />
        </TabsContent>

        <TabsContent value="driving">
          {p.permis_conducere && <DrivingHistoryPane personId={id}/>}
        </TabsContent>

        <TabsContent value="meldeverfahren">
          {p.is_subcontractor && (
            <MeldeverfahrenTab
              personId={id}
              personName={`${p.nume || ""} ${p.prenume || ""}`.trim()}
              isSubcontractor={!!p.is_subcontractor}
            />
          )}
        </TabsContent>

        <TabsContent value="daune">
          <DamagesPane
            lockedPersonId={id}
            showFilters={false}
            title={`Daune înregistrate pentru ${(p.nume || "") + " " + (p.prenume || "")}`.trim()}
          />
        </TabsContent>

        <TabsContent value="psa">
          <PSAPane personId={id} personName={`${p.nume || ""} ${p.prenume || ""}`.trim()} />
        </TabsContent>

        <TabsContent value="cronologie">
          <EquipmentTimelinePane personId={id} />
        </TabsContent>

        {showTagesraport && (
          <TabsContent value="tagesraport">
            <div className="card-flat p-5">
              <div className="mb-3">
                <h3 className="font-display text-lg font-bold flex items-center gap-2">📝 Tagesraport</h3>
                <p className="text-xs text-zinc-500">Raport zilnic — ce task-uri ai făcut și cât timp a luat fiecare. HH:MM = ore:minute.</p>
              </div>
              <TagesraportPanel
                personId={id}
                personName={`${p.prenume || ""} ${p.nume || ""}`.trim()}
                canEdit={isAdmin || authUser?.person_id === id}
              />
            </div>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="monthly-reports">
            <MonthlyReportsPane personId={id} personName={p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim()} />
          </TabsContent>
        )}

        {(isAdmin || authUser?.person_id === id) && (
          <TabsContent value="cheltuieli">
            <MyExpensesPane personId={id} isAdmin={isAdmin} />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="cont">
            <UserAccountPane person={p} />
          </TabsContent>
        )}
      </Tabs>

      {dismissalOpen && (
        <DismissalDialog
          person={p}
          onClose={() => setDismissalOpen(false)}
          onDone={() => { setDismissalOpen(false); load(); }}
        />
      )}

      {assignedOpen && (
        <AssignedItemsDialog
          person={p}
          onClose={() => setAssignedOpen(false)}
        />
      )}

      {rehireOpen && (
        <RehireDialog
          person={p}
          onClose={() => setRehireOpen(false)}
          onDone={() => { setRehireOpen(false); load(); }}
        />
      )}
    </div>
  );
}



function F({ label, v, onChange, type = "text", disabled, textarea, className = "" }) {
  return (
    <div className={className}>
      <Label className="small-label">{label}</Label>
      {textarea ? (
        <Textarea className="mt-2" rows={3} value={v || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
      ) : (
        <Input className="mt-2" type={type} value={v || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
      )}
    </div>
  );
}

const PERSON_DOC_TYPES = ["Buletin / Carte de identitate", "Permis de ședere elvețian", "Permis de conducere", "Pașaport", "Contract de muncă", "Kündigung", "Adeverință medicală", "Certificat de naștere", "Diplomă", "Alte documente"];

// Tipurile de documente pentru care data expirării e relevantă (afișată/cerută)
const DOC_TYPES_WITH_EXPIRY = new Set(["Permis de ședere elvețian", "Permis de conducere", "Pașaport", "Buletin / Carte de identitate", "Adeverință medicală"]);
const DOC_TYPES_EXPIRY_REQUIRED = new Set(["Permis de ședere elvețian"]);
// Documente care DOAR pot fi PDF (acte de identitate)
const DOC_TYPES_PDF_ONLY = new Set(["Buletin / Carte de identitate", "Pașaport"]);

const isPdfFile = (file) => {
  if (!file) return false;
  const ct = (file.content_type || file.mime_type || "").toLowerCase();
  if (ct === "application/pdf") return true;
  const name = (file.original_filename || file.filename || file.name || "").toLowerCase();
  return name.endsWith(".pdf");
};

function docExpiryStatus(d) {
  if (!d?.expires_at) return null;
  const exp = new Date(d.expires_at);
  if (isNaN(exp.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.floor((exp - today) / (24 * 3600 * 1000));
  if (days < 0) return { kind: "expired", days, exp };
  if (days <= 14) return { kind: "soon", days, exp };
  if (days <= 60) return { kind: "warn", days, exp };
  return { kind: "ok", days, exp };
}

function Documente({ person, onChanged, canEdit }) {
  const [pendingFile, setPendingFile] = useState(null);
  const [pickedType, setPickedType] = useState(PERSON_DOC_TYPES[0]);
  const [customType, setCustomType] = useState("");
  const [pendingExpiry, setPendingExpiry] = useState("");
  // Permis de ședere — flow special cu 2 fișiere + 1 dată expirare
  const [permisOpen, setPermisOpen] = useState(false);
  const [permisFront, setPermisFront] = useState(null);
  const [permisBack, setPermisBack] = useState(null);
  const [permisExpiry, setPermisExpiry] = useState("");
  const [permisSaving, setPermisSaving] = useState(false);

  const onUploaded = (f) => {
    setPendingFile(f);
    setPickedType(PERSON_DOC_TYPES[0]);
    setCustomType("");
    setPendingExpiry("");
  };

  const savePermisSedere = async () => {
    if (!permisFront || !permisBack) {
      toast.error("Încarcă ambele părți (față și verso)");
      return;
    }
    if (!permisExpiry) {
      toast.error("Data expirării este obligatorie");
      return;
    }
    setPermisSaving(true);
    try {
      const nowIso = new Date().toISOString();
      const newDocs = [
        {
          file_id: permisFront.id,
          name: permisFront.original_filename || permisFront.filename || permisFront.name || "permis-fata",
          doc_type: "Permis de ședere elvețian",
          side: "front",
          expires_at: permisExpiry,
          uploaded_at: nowIso,
        },
        {
          file_id: permisBack.id,
          name: permisBack.original_filename || permisBack.filename || permisBack.name || "permis-verso",
          doc_type: "Permis de ședere elvețian",
          side: "back",
          expires_at: permisExpiry,
          uploaded_at: nowIso,
        },
      ];
      // Înlocuiește orice permis de ședere existent — un permis activ = 2 părți (față + verso)
      const rePermis = /permis\s*(de\s*)?([sșş][eă]?dere|aufenthalt)|aufenthaltsbewilligung|ausweis\s*[bclg]/i;
      const remaining = (person.documente || []).filter((d) => !rePermis.test(d.doc_type || "") && !rePermis.test(d.name || ""));
      await api.put(`/persons/${person.id}`, { documente: [...remaining, ...newDocs] });
      toast.success("Permis de ședere salvat (față + verso, expiră " + permisExpiry + ")");
      setPermisOpen(false);
      setPermisFront(null);
      setPermisBack(null);
      setPermisExpiry("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setPermisSaving(false);
    }
  };
  const confirmType = async () => {
    if (!pendingFile) return;
    const finalType = pickedType === "Alte documente" && customType.trim()
      ? customType.trim()
      : pickedType;
    if (DOC_TYPES_PDF_ONLY.has(finalType) && !isPdfFile(pendingFile)) {
      toast.error(`${finalType} trebuie încărcat doar în format PDF`);
      return;
    }
    if (DOC_TYPES_EXPIRY_REQUIRED.has(finalType) && !pendingExpiry) {
      toast.error("Data expirării este obligatorie pentru acest document");
      return;
    }
    const docs = [
      ...(person.documente || []),
      {
        file_id: pendingFile.id,
        name: pendingFile.original_filename || pendingFile.filename || pendingFile.name || "document",
        doc_type: finalType,
        expires_at: pendingExpiry || null,
        uploaded_at: new Date().toISOString(),
      },
    ];
    await api.put(`/persons/${person.id}`, { documente: docs });
    toast.success(`${finalType} adăugat`);
    setPendingFile(null);
    setPendingExpiry("");
    onChanged();
  };
  const remove = async (fileId) => {
    const docs = (person.documente || []).filter((d) => d.file_id !== fileId);
    await api.put(`/persons/${person.id}`, { documente: docs });
    onChanged();
  };
  const updateType = async (fileId, newType) => {
    const docs = person.documente || [];
    const targetDoc = docs.find((d) => d.file_id === fileId);
    if (DOC_TYPES_PDF_ONLY.has(newType) && targetDoc && !isPdfFile(targetDoc)) {
      toast.error(`${newType} trebuie încărcat doar în format PDF. Reîncarcă fișierul ca PDF.`);
      return;
    }
    const next = docs.map((d) => d.file_id === fileId ? { ...d, doc_type: newType } : d);
    await api.put(`/persons/${person.id}`, { documente: next });
    onChanged();
  };
  const updateExpiry = async (fileId, newDate) => {
    const docs = (person.documente || []).map((d) => d.file_id === fileId ? { ...d, expires_at: newDate || null } : d);
    await api.put(`/persons/${person.id}`, { documente: docs });
    onChanged();
  };

  // showExpiry pentru tipul curent ales în dialogul pendingFile
  const showExpiryForPending = DOC_TYPES_WITH_EXPIRY.has(pickedType);
  const requireExpiryForPending = DOC_TYPES_EXPIRY_REQUIRED.has(pickedType);

  return (
    <div className="card-flat p-6">
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <UploadButton testId="upload-document" label="Încarcă document" accept="application/pdf,image/jpeg,image/png,image/heic,image/heif" onUploaded={onUploaded} />
          <Button
            type="button"
            variant="outline"
            onClick={() => setPermisOpen(true)}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="upload-permis-sedere-btn"
            title="Încarcă față + verso ale permisului de ședere, cu o singură dată de expirare"
          >
            🪪 Încarcă permis de ședere (față + verso)
          </Button>
        </div>
      )}
      {pendingFile && (
        <Dialog open={!!pendingFile} onOpenChange={(o) => { if (!o) setPendingFile(null); }}>
          <DialogContent data-testid="doc-type-dialog">
            <DialogHeader>
              <DialogTitle>Tip document</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Categorie</Label>
                <select
                  value={pickedType}
                  onChange={(e) => setPickedType(e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded border border-zinc-300"
                  data-testid="doc-type-select"
                >
                  {PERSON_DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {pickedType === "Alte documente" && (
                <div>
                  <Label>Titlu custom</Label>
                  <Input value={customType} onChange={(e) => setCustomType(e.target.value)} placeholder="ex: Diplomă bac" />
                </div>
              )}
              {DOC_TYPES_PDF_ONLY.has(pickedType) && pendingFile && !isPdfFile(pendingFile) && (
                <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="doc-pdf-only-warning">
                  ⚠ <strong>{pickedType}</strong> trebuie să fie încărcat doar în format <strong>PDF</strong>. Fișierul curent ({pendingFile.original_filename || pendingFile.filename || pendingFile.name || "—"}) nu este PDF. Anulează și reîncarcă un PDF.
                </div>
              )}
              {showExpiryForPending && (
                <div>
                  <Label>
                    Data expirării
                    {requireExpiryForPending && <span className="text-red-600 ml-1">*</span>}
                  </Label>
                  <Input
                    type="date"
                    value={pendingExpiry}
                    onChange={(e) => setPendingExpiry(e.target.value)}
                    className={`mt-1 ${requireExpiryForPending && !pendingExpiry ? "border-red-400" : ""}`}
                    data-testid="doc-expiry-input"
                  />
                  {requireExpiryForPending && (
                    <p className="text-[11px] text-red-600 mt-1">Obligatoriu pentru permisul de ședere.</p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPendingFile(null)}>Anulează</Button>
              <Button onClick={confirmType} data-testid="doc-confirm">Salvează</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        {(person.documente || []).map((d) => {
          const status = docExpiryStatus(d);
          const showExpiryUI = canEdit && (DOC_TYPES_WITH_EXPIRY.has(d.doc_type) || d.expires_at);
          return (
            <div key={d.file_id} className="relative" data-testid={`person-doc-${d.file_id}`}>
              <div className="mb-1.5">
                {canEdit ? (
                  <select
                    value={PERSON_DOC_TYPES.includes(d.doc_type) ? d.doc_type : "Alte documente"}
                    onChange={(e) => {
                      if (e.target.value === "Alte documente") {
                        const v = window.prompt("Titlu document:", d.doc_type && !PERSON_DOC_TYPES.includes(d.doc_type) ? d.doc_type : "");
                        if (v && v.trim()) updateType(d.file_id, v.trim());
                      } else {
                        updateType(d.file_id, e.target.value);
                      }
                    }}
                    className="text-[11px] font-bold text-[var(--primary)] bg-[var(--primary-subtle)] px-2 py-0.5 rounded w-full border-0 cursor-pointer"
                    data-testid={`person-doc-type-${d.file_id}`}
                  >
                    {PERSON_DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    {d.doc_type && !PERSON_DOC_TYPES.includes(d.doc_type) && (
                      <option value={d.doc_type}>{d.doc_type}</option>
                    )}
                  </select>
                ) : (
                  <div className="text-[11px] font-bold text-[var(--primary)] bg-[var(--primary-subtle)] px-2 py-0.5 rounded text-center truncate" title={d.doc_type || "Document"}>
                    {d.doc_type || "Document"}
                  </div>
                )}
                {d.side && (
                  <div className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 mt-1 text-center" data-testid={`person-doc-side-${d.file_id}`}>
                    {d.side === "front" ? "🪪 FAȚĂ" : "🔄 VERSO"}
                  </div>
                )}
              </div>
              <DocumentTile fileId={d.file_id} name={d.name} />
              {showExpiryUI && (
                <div className="mt-1.5">
                  <Input
                    type="date"
                    value={d.expires_at || ""}
                    onChange={(e) => updateExpiry(d.file_id, e.target.value)}
                    className={`h-7 text-[11px] ${status?.kind === "expired" ? "border-red-500 bg-red-50 text-red-700 font-bold" : status?.kind === "soon" ? "border-amber-500 bg-amber-50 text-amber-800 font-medium" : ""}`}
                    title="Data expirării"
                    data-testid={`doc-expiry-${d.file_id}`}
                  />
                  {status && (
                    <div className={`text-[10px] mt-0.5 font-medium ${status.kind === "expired" ? "text-red-600" : status.kind === "soon" ? "text-amber-700" : status.kind === "warn" ? "text-zinc-600" : "text-emerald-700"}`}>
                      {status.kind === "expired" ? `⚠ EXPIRAT acum ${-status.days} zile` :
                        status.kind === "soon" ? `⚠ Expiră în ${status.days} zile` :
                        `Expiră în ${status.days} zile`}
                    </div>
                  )}
                </div>
              )}
              {!canEdit && d.expires_at && status && (
                <div className={`mt-1 text-[10px] font-bold ${status.kind === "expired" ? "text-red-600" : status.kind === "soon" ? "text-amber-700" : "text-zinc-500"}`}>
                  {status.kind === "expired" ? `EXPIRAT acum ${-status.days} zile` : `Expiră: ${d.expires_at}`}
                </div>
              )}
              {canEdit && (
                <button
                  onClick={() => remove(d.file_id)}
                  className="absolute bottom-1.5 left-1.5 bg-white p-1.5 rounded-full shadow-md border border-red-200 z-10 hover:bg-red-50 hover:border-red-400"
                  title="Șterge documentul"
                  data-testid={`doc-remove-${d.file_id}`}
                >
                  <Trash2 size={13} className="text-red-600" />
                </button>
              )}
            </div>
          );
        })}
        {(person.documente || []).length === 0 && <div className="text-sm text-zinc-500 col-span-full">Niciun document.</div>}
      </div>

      {pendingFile && (
        <Dialog open onOpenChange={(v) => !v && setPendingFile(null)}>
          <DialogContent className="max-w-sm" data-testid="person-doc-type-dialog">
            <DialogHeader>
              <DialogTitle>Tip document</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-zinc-600">Ce reprezintă acest fișier? <span className="font-mono text-xs text-zinc-400">({pendingFile.original_filename || pendingFile.filename || "fișier"})</span></p>
              <div className="grid grid-cols-2 gap-2">
                {PERSON_DOC_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setPickedType(t)}
                    className={`text-xs px-3 py-2 rounded-md border transition-colors ${pickedType === t ? "bg-[var(--primary)] text-white border-[var(--primary)]" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"}`}
                    data-testid={`person-doc-type-opt-${t.replace(/[\s/]+/g, "-").toLowerCase()}`}
                  >{t}</button>
                ))}
              </div>
              {pickedType === "Alte documente" && (
                <Input
                  placeholder="Specifică tipul"
                  value={customType}
                  onChange={(e) => setCustomType(e.target.value)}
                  autoFocus
                  data-testid="person-doc-type-custom"
                />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPendingFile(null)}>Anulează</Button>
              <Button onClick={confirmType} className="bg-[var(--primary)]" data-testid="person-doc-type-confirm">Salvează</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog dedicat: Permis de ședere (față + verso + 1 dată expirare) */}
      {permisOpen && (
        <Dialog open onOpenChange={(v) => !v && !permisSaving && setPermisOpen(false)}>
          <DialogContent className="max-w-md" data-testid="permis-sedere-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">🪪 Permis de ședere elvețian</DialogTitle>
            </DialogHeader>
            <div className="text-xs text-zinc-500 -mt-1">
              Încarcă ambele părți ale permisului. Data expirării este aceeași pentru ambele.
              {(person.documente || []).some((d) => /permis\s*(de\s*)?([sșş][eă]?dere|aufenthalt)|aufenthaltsbewilligung|ausweis\s*[bclg]/i.test(d.doc_type || "")) && (
                <div className="mt-2 text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  ⚠ Există deja un permis salvat. Salvarea înlocuiește versiunea curentă (ambele părți).
                </div>
              )}
            </div>
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                {/* Front */}
                <div className="border-2 border-dashed border-zinc-300 rounded-lg p-3 text-center bg-zinc-50">
                  <div className="text-[10px] uppercase font-bold tracking-wider text-zinc-600 mb-1.5">FAȚĂ (Vorderseite)</div>
                  {permisFront ? (
                    <div className="text-[11px] bg-emerald-100 text-emerald-800 border border-emerald-300 rounded px-2 py-1.5">
                      ✓ {permisFront.original_filename || permisFront.filename || "fișier"}
                      <button
                        type="button"
                        onClick={() => setPermisFront(null)}
                        className="ml-1.5 text-emerald-700 hover:text-emerald-900 underline text-[10px]"
                        data-testid="permis-front-clear"
                      >Schimbă</button>
                    </div>
                  ) : (
                    <UploadButton
                      testId="permis-front-upload"
                      label="Selectează față"
                      accept="application/pdf,image/jpeg,image/png,image/heic,image/heif"
                      onUploaded={setPermisFront}
                    />
                  )}
                </div>
                {/* Back */}
                <div className="border-2 border-dashed border-zinc-300 rounded-lg p-3 text-center bg-zinc-50">
                  <div className="text-[10px] uppercase font-bold tracking-wider text-zinc-600 mb-1.5">VERSO (Rückseite)</div>
                  {permisBack ? (
                    <div className="text-[11px] bg-emerald-100 text-emerald-800 border border-emerald-300 rounded px-2 py-1.5">
                      ✓ {permisBack.original_filename || permisBack.filename || "fișier"}
                      <button
                        type="button"
                        onClick={() => setPermisBack(null)}
                        className="ml-1.5 text-emerald-700 hover:text-emerald-900 underline text-[10px]"
                        data-testid="permis-back-clear"
                      >Schimbă</button>
                    </div>
                  ) : (
                    <UploadButton
                      testId="permis-back-upload"
                      label="Selectează verso"
                      accept="application/pdf,image/jpeg,image/png,image/heic,image/heif"
                      onUploaded={setPermisBack}
                    />
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs">Data expirării <span className="text-red-600">*</span></Label>
                <Input
                  type="date"
                  value={permisExpiry}
                  onChange={(e) => setPermisExpiry(e.target.value)}
                  className="mt-1"
                  data-testid="permis-expiry-input"
                  required
                />
                <p className="text-[10px] text-zinc-500 mt-1">Aceeași dată pentru ambele părți.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPermisOpen(false)} disabled={permisSaving}>Anulează</Button>
              <Button
                onClick={savePermisSedere}
                disabled={permisSaving || !permisFront || !permisBack || !permisExpiry}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="permis-save-btn"
              >
                {permisSaving ? "Se salvează…" : "💾 Salvează permis"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export function CalendarPane({ personId, canEdit, defaultRate = 0, personName = "" }) {
  const { isAdmin } = useAuth();
  const today = new Date();
  const [month, setMonth] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  const [entries, setEntries] = useState([]);
  const [filterDay, setFilterDay] = useState("");
  const [minHours, setMinHours] = useState("");
  const [allocByDate, setAllocByDate] = useState({}); // { 'YYYY-MM-DD': [{id, label, is_regie}] }
  const [allProjects, setAllProjects] = useState([]); // fallback dropdown if no allocation
  const [allRegii, setAllRegii] = useState([]); // regii disponibile pentru selecție
  const [aconturi, setAconturi] = useState([]);
  const [acontSources, setAcontSources] = useState([
    "Avans cash", "Avans bancar", "Reținere", "Bonus", "Penalizare", "Diurnă suplimentară", "Altă sursă",
  ]);
  const [acontDialog, setAcontDialog] = useState(null);
  const [missingReports, setMissingReports] = useState({}); // {date: bool hasReport} pentru zilele cu magazie
  const [tagesraportDialog, setTagesraportDialog] = useState(null); // {date, person_name} pentru quick-create
  const [editDayDialog, setEditDayDialog] = useState(null); // { date, entries } — editor multi-entry pe zi

  const load = async () => {
    const { data } = await api.get(`/persons/${personId}/calendar?month=${month}`);
    setEntries(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, personId]);

  // Fetch tagesraport status pentru zilele cu magazie (doar pentru Khreis/Serghei/Lorena)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/persons/${personId}/calendar/tagesraport-status?month=${month}`);
        setMissingReports(data || {});
      } catch { setMissingReports({}); }
    })();
  }, [month, personId, entries]);

  // Load schedule entries (regii + projects) where this person is allocated, for the current month
  useEffect(() => {
    (async () => {
      try {
        const [sched, projs, srcs, rgs] = await Promise.all([
          api.get(`/schedule?month=${month}`),
          api.get(`/projects`).catch(() => ({ data: [] })),
          api.get(`/acont-sources`).catch(() => ({ data: null })),
          api.get(`/regii`).catch(() => ({ data: [] })),
        ]);
        if (Array.isArray(srcs.data) && srcs.data.length > 0) setAcontSources(srcs.data);
        setAllProjects(projs.data || []);
        setAllRegii(Array.isArray(rgs.data) ? rgs.data : []);
        const map = {};
        for (const e of (sched.data || [])) {
          if (!Array.isArray(e.assigned_person_ids) || !e.assigned_person_ids.includes(personId)) continue;
          const start = new Date(e.start_date);
          const end = new Date(e.end_date || e.start_date);
          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const ds = d.toISOString().slice(0, 10);
            if (!map[ds]) map[ds] = [];
            const projTitle = e.project_name || e.label || "Proiect";
            map[ds].push({
              id: e.project_id || `regie-${e.id}`,
              label: e.is_regie ? `${e.label || projTitle} (regie)` : projTitle,
              is_regie: !!e.is_regie,
            });
          }
        }
        setAllocByDate(map);
      } catch {}
    })();
  }, [month, personId]);

  // Load ACONT entries (admin + persoana respectivă pot vedea)
  const loadAconturi = async () => {
    try {
      const { data } = await api.get(`/persons/${personId}/aconturi?month=${month}`);
      setAconturi(data || []);
    } catch { setAconturi([]); }
  };
  useEffect(() => { loadAconturi(); /* eslint-disable-next-line */ }, [month, personId]);

  // O persoană poate avea MAI MULTE entries pe aceeași zi (multi-șantier/magazie/birou).
  // entriesByDate[date] = lista de entries pe zi
  // singleByDate[date] = primul entry (pentru UI simplificat când e doar unul)
  const entriesByDate = useMemo(() => {
    const m = {};
    for (const e of entries) {
      if (!m[e.date]) m[e.date] = [];
      m[e.date].push(e);
    }
    return m;
  }, [entries]);
  const byDate = useMemo(() => {
    const m = {};
    for (const [date, list] of Object.entries(entriesByDate)) {
      m[date] = list[0];
    }
    return m;
  }, [entriesByDate]);

  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();

  // Salvează ziua folosind PUT /calendar/day (suportă multi-entry + auto-sync tagesraport).
  // În UI single-entry, trimitem o listă cu un singur item; kind detectat din opțiunea selectată.
  const saveDay = async (dateStr, items) => {
    try {
      await api.put(`/persons/${personId}/calendar/day`, {
        date: dateStr,
        hourly_rate: parseFloat(items[0]?.hourly_rate) || parseFloat(defaultRate) || 0,
        entries: items.map((it) => ({
          kind: it.kind || "project",
          project_id: it.project_id || null,
          regie_id: it.regie_id || null,
          label: it.label || null,
          hours: parseFloat(it.hours) || 0,
          hours_driven: parseFloat(it.hours_driven) || 0,
          color: it.color || null,
          note: it.note || null,
        })),
      });
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Eroare la salvare";
      toast.error(`Calendar: ${msg}`);
      console.error("saveDay failed", { dateStr, items, err });
    }
  };

  const setDay = async (dateStr, hours, rate, projectId, color, extras = {}) => {
    const existing = byDate[dateStr];
    // Detectează kind din projectId magic values: "__magazie__" / "__birou__" / "regie:<id>"
    let kind = "project";
    let label = null;
    let pid_clean = projectId;
    let regie_id_clean = null;
    if (projectId === "__magazie__") { kind = "magazie"; label = "Magazie"; pid_clean = null; }
    else if (projectId === "__birou__") { kind = "birou"; label = "Birou"; pid_clean = null; }
    else if (typeof projectId === "string" && projectId.startsWith("regie:")) {
      kind = "regie";
      regie_id_clean = projectId.slice("regie:".length);
      pid_clean = null;
    }
    else if (!projectId) { kind = existing?.kind || "project"; label = existing?.project_label; pid_clean = null; }

    const item = {
      kind,
      project_id: kind === "project" ? pid_clean : null,
      regie_id: kind === "regie" ? regie_id_clean : null,
      label,
      hours: hours,
      hours_driven: extras.hours_driven !== undefined ? extras.hours_driven : (existing?.hours_driven || 0),
      hourly_rate: rate,
      color: color !== undefined ? color : (existing?.color || null),
    };
    await saveDay(dateStr, [item]);
  };

  const filtered = entries.filter((e) => {
    if (filterDay && !e.date.endsWith(`-${filterDay.padStart(2, "0")}`)) return false;
    if (minHours && e.hours < parseFloat(minHours)) return false;
    return true;
  });

  const acontTotal = useMemo(() => aconturi.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0), [aconturi]);

  const fmtNum = (n) => parseFloat((Number(n) || 0).toFixed(2));

  const totals = filtered.reduce(
    (acc, e) => ({
      days: acc.days + (e.hours > 0 ? 1 : 0),
      hours: acc.hours + (e.hours || 0),
      driven: acc.driven + (e.hours_driven || 0),
      total: acc.total + (e.total || 0),
      diurna: acc.diurna + (e.diurna || 0),
    }),
    { days: 0, hours: 0, driven: 0, total: 0, diurna: 0 }
  );
  // Zile UNICE lucrate (multiple entries pe aceeași zi → 1 singură zi)
  const uniqueDays = new Set(filtered.filter((e) => (e.hours || 0) > 0 || (e.hours_driven || 0) > 0).map((e) => e.date));
  totals.days = uniqueDays.size;
  const totalNet = totals.total + acontTotal;

  const removeAcont = async (aid) => {
    if (!(await confirm({ body: "Ștergi această intrare ACONT?", danger: true }))) return;
    try {
      await api.delete(`/persons/${personId}/aconturi/${aid}`);
      setAconturi((x) => x.filter((a) => a.id !== aid));
      toast.success("Șters");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  return (
    <div className="card-flat p-6">
      <MissingHoursBanner scope="person" personId={personId} />
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="small-label">Luna</Label>
          <MonthPicker value={month} onChange={setMonth} className="mt-2" testId="calendar-month" />
        </div>
        <div>
          <Label className="small-label">Filtru zi</Label>
          <Input type="number" min={1} max={31} value={filterDay} onChange={(e) => setFilterDay(e.target.value)} className="mt-2 w-28" placeholder="ex: 15" />
        </div>
        <div>
          <Label className="small-label">Min ore</Label>
          <Input type="number" step="0.5" value={minHours} onChange={(e) => setMinHours(e.target.value)} className="mt-2 w-28" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <Stat label="Zile lucrate" v={totals.days} />
        <Stat label="Ore lucrate" v={fmtNum(totals.hours)} />
        <Stat label="🚗 Ore conduse" v={fmtNum(totals.driven)} color="#D97706" />
        <Stat label="Diurnă (CHF)" v={totals.diurna.toFixed(2)} />
        <Stat label="Subtotal (CHF)" v={totals.total.toFixed(2)} color="#0044FF" />
        <Stat
          label={`Total + ACONT (CHF)`}
          v={totalNet.toFixed(2)}
          color={acontTotal < 0 ? "#DC2626" : "#16A34A"}
          testId="total-with-acont"
        />
      </div>

      {acontTotal !== 0 && (
        <div className="text-[12px] text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 mb-4 inline-block">
          ACONT lună: <strong className={acontTotal < 0 ? "text-red-600" : "text-green-600"}>
            {acontTotal >= 0 ? "+" : ""}{acontTotal.toFixed(2)} CHF
          </strong>
        </div>
      )}

      {defaultRate > 0 && (
        <div className="text-[12px] text-zinc-600 bg-[var(--primary-subtle)] border border-[var(--primary)]/20 rounded-md px-3 py-2 mb-4 inline-block" data-testid="auto-rate-banner">
          💰 Tarif orar implicit din profil: <strong className="text-[var(--primary)]">{defaultRate} CHF/h</strong> — se completează automat când introduci orele.
        </div>
      )}

      <div className="grid grid-cols-7 gap-2">
        {["L","M","M","J","V","S","D"].map((d, i) => <div key={i} className="text-xs font-semibold text-zinc-500 text-center py-1">{d}</div>)}
        {(() => {
          const first = new Date(y, m - 1, 1).getDay(); // 0 = Sun
          const pad = (first + 6) % 7; // convert to Monday-first
          return Array.from({ length: pad }).map((_, i) => <div key={"e"+i} />);
        })()}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const dateStr = `${month}-${String(day).padStart(2, "0")}`;
          const dayList = entriesByDate[dateStr] || [];
          const e = byDate[dateStr];
          const totalHours = dayList.reduce((s, x) => s + (parseFloat(x.hours) || 0), 0);
          const totalDriven = dayList.reduce((s, x) => s + (parseFloat(x.hours_driven) || 0), 0);
          const worked = totalHours > 0 || totalDriven > 0;
          const isToday = new Date().toISOString().slice(0, 10) === dateStr;
          const bulletColor = e?.color || (e?.project_id ? "#0044FF" : "#94a3b8");
          const hasMagazie = dayList.some((x) => x.kind === "magazie");
          const needsReport = hasMagazie && missingReports[dateStr] === false;
          return (
            <div key={dateStr} className={`cal-cell ${worked ? "worked" : ""} ${isToday ? "today" : ""} ${needsReport ? "ring-2 ring-red-400" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {dayList.map((it, idx) => {
                    const col = it.color || (it.kind === "magazie" ? "#0EA5E9" : it.kind === "birou" ? "#8B5CF6" : it.project_id ? "#0044FF" : "#94a3b8");
                    return (
                      <span key={idx} className="w-2.5 h-2.5 rounded-full ring-1 ring-white shadow-sm" style={{ background: col }} title={it.project_label || it.kind} />
                    );
                  })}
                  {worked && canEdit && dayList.length <= 1 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid={`color-bullet-${dateStr}`}
                          className="w-2.5 h-2.5 rounded-full ring-1 ring-white shadow-sm hover:scale-125 transition-transform cursor-pointer opacity-0 hover:opacity-100 absolute"
                          style={{ background: bulletColor }}
                          title="Click pentru a schimba culoarea"
                        />
                      </PopoverTrigger>
                      <PopoverContent className="w-48 p-2" align="start">
                        <div className="text-[10px] font-semibold text-zinc-500 uppercase mb-2 px-1">Culoare bulină</div>
                        <div className="grid grid-cols-4 gap-1.5">
                          {BULLET_PALETTE.map((c) => (
                            <button
                              key={c.hex}
                              type="button"
                              onClick={() => setDay(dateStr, e.hours, e.hourly_rate, e.project_id, c.hex)}
                              className={`w-8 h-8 rounded-full ring-2 hover:scale-110 transition-transform ${e?.color === c.hex ? "ring-zinc-900" : "ring-white"}`}
                              style={{ background: c.hex }}
                              title={c.label}
                              data-testid={`color-opt-${c.hex.replace("#","")}`}
                            />
                          ))}
                        </div>
                        {e?.color && (
                          <button
                            type="button"
                            onClick={() => setDay(dateStr, e.hours, e.hourly_rate, e.project_id, "")}
                            className="mt-2 text-[10px] text-zinc-500 hover:text-zinc-900 underline w-full text-center"
                          >Reset (auto)</button>
                        )}
                      </PopoverContent>
                    </Popover>
                  )}
                  <div className="text-xs font-semibold">{day}</div>
                </div>
                {worked && (
                  <div className="text-[10px] text-right leading-none">
                    <div className="text-[var(--primary)] font-semibold" data-testid={`day-hours-${dateStr}`}>{fmtNum(totalHours)}h</div>
                    {totalDriven > 0 && <div className="text-amber-700 font-semibold mt-0.5" data-testid={`day-driven-${dateStr}`}>🚗 {totalDriven}h</div>}
                  </div>
                )}
              </div>
              {dayList.length > 1 && (
                <div className="mt-1 text-[10px] text-zinc-600 leading-tight" data-testid={`multi-day-${dateStr}`}>
                  {dayList.map((it, idx) => (
                    <div key={idx} className="truncate">• {it.project_label || (it.kind === "magazie" ? "🏪 Magazie" : it.kind === "birou" ? "🏢 Birou" : "—")}: {it.hours}h</div>
                  ))}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => setEditDayDialog({ date: dateStr, entries: dayList })}
                      className="mt-1 w-full text-[10px] font-semibold bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5 transition-colors flex items-center justify-center gap-1"
                      data-testid={`edit-multi-day-${dateStr}`}
                      title="Editează individual fiecare alocare a zilei"
                    >
                      <Pencil size={10}/> Editează ziua
                    </button>
                  )}
                </div>
              )}
              {dayList.length <= 1 && (
                <>
                  <div className="mt-1 flex gap-1">
                    <Input
                      type="number" step="0.5" min="0" disabled={!canEdit}
                      value={e?.hours ?? ""} placeholder="ore"
                      onChange={(ev) => setDay(dateStr, ev.target.value, e?.hourly_rate ?? defaultRate, e?.project_id || (e?.kind === "magazie" ? "__magazie__" : e?.kind === "birou" ? "__birou__" : null))}
                      className="h-7 text-xs px-1"
                      data-testid={`hours-input-${dateStr}`}
                    />
                    <Input
                      type="number" step="0.5" min="0" disabled={!canEdit}
                      value={e?.hours_driven ?? ""}
                      placeholder="🚗h"
                      onChange={(ev) => setDay(dateStr, e?.hours || 0, e?.hourly_rate ?? defaultRate, e?.project_id || (e?.kind === "magazie" ? "__magazie__" : e?.kind === "birou" ? "__birou__" : null), undefined, { hours_driven: parseFloat(ev.target.value) || 0 })}
                      className="h-7 text-xs px-1 border-amber-300/60 placeholder:text-amber-700"
                      data-testid={`driven-input-${dateStr}`}
                      title="Ore conduse"
                    />
                  </div>
                </>
              )}
              {dayList.length <= 1 && e && (e.project_label || e.kind === "magazie" || e.kind === "birou") && (
                <div className="mt-1 text-[10px] truncate font-semibold flex items-center gap-1" title={e.project_label || (e.kind === "magazie" ? "Magazie" : "Birou")} data-testid={`proj-label-${dateStr}`}>
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: e.color || (e.kind === "magazie" ? "#0EA5E9" : e.kind === "birou" ? "#8B5CF6" : "#0044FF") }} />
                  <span className="truncate" style={{ color: e.color || (e.kind === "magazie" ? "#0891B2" : e.kind === "birou" ? "#7C3AED" : "#1E40AF") }}>
                    {e.kind === "magazie" ? "🏪 Magazie" : e.kind === "birou" ? "🏢 Birou" : e.project_label}
                  </span>
                </div>
              )}
              {(canEdit || e?.project_label) && dayList.length <= 1 && (() => {
                const dayAllocs = allocByDate[dateStr] || [];
                // Build options: prefer day allocations, fallback to all projects + regii + currently saved
                const optsMap = new Map();
                for (const a of dayAllocs) optsMap.set(a.id, a.label);
                for (const p of allProjects) if (!optsMap.has(p.id) && projectActiveOnDay(p, dateStr)) optsMap.set(p.id, p.name || p.title || p.label || "Proiect");
                if (e?.project_id && !optsMap.has(e.project_id)) optsMap.set(e.project_id, e.project_label || "Proiect");
                const opts = [...optsMap.entries()];
                // Regii: cheie prefixată cu "regie:" ca să distingem de proiecte — doar cele active pe zi
                const regiiOpts = (allRegii || []).filter((r) => regieActiveOnDay(r, dateStr)).map((r) => [`regie:${r.id}`, `⚙ ${r.name || "Regie"}`]);
                // Ensure current regie value is in options
                if (e?.kind === "regie" && e?.regie_id) {
                  const rk = `regie:${e.regie_id}`;
                  if (!regiiOpts.find((x) => x[0] === rk)) {
                    regiiOpts.push([rk, `⚙ ${e.project_label || "Regie"}`]);
                  }
                }
                let currentVal = "__none__";
                if (e?.kind === "magazie") currentVal = "__magazie__";
                else if (e?.kind === "birou") currentVal = "__birou__";
                else if (e?.kind === "regie" && e?.regie_id) currentVal = `regie:${e.regie_id}`;
                else if (e?.project_id) currentVal = e.project_id;
                return (
                  <ProjectSearchSelect
                    value={currentVal}
                    onValueChange={(v) => setDay(dateStr, e?.hours || 0, e?.hourly_rate ?? defaultRate, v === "__none__" ? null : v)}
                    disabled={!canEdit}
                    opts={opts}
                    regiiOpts={regiiOpts}
                    hasDayAllocs={dayAllocs.length > 0}
                    testid={`proj-${dateStr}`}
                    placeholder={dayAllocs.length > 0 ? "Alege proiect/regie" : "—"}
                  />
                );
              })()}
              {needsReport && (
                <div className="mt-1 space-y-1" data-testid={`missing-tagesraport-wrap-${dateStr}`}>
                  <div className="text-[9px] text-red-700 font-bold bg-red-50 border border-red-300 rounded px-1.5 py-0.5 leading-tight flex items-center gap-1" data-testid={`missing-tagesraport-${dateStr}`}>
                    ⚠️ Lipsă Tagesraport
                  </div>
                  <button
                    type="button"
                    onClick={() => setTagesraportDialog({ date: dateStr, person_id: personId, person_name: personName })}
                    className="text-[9px] bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded px-1.5 py-0.5 w-full text-center transition-colors"
                    data-testid={`add-tagesraport-${dateStr}`}
                  >+ Tagesraport</button>
                </div>
              )}
              {/* Linie audit — cine a băgat / cine a modificat orele (vizibil pe celulă) */}
              {dayList.length > 0 && dayList[0]?.created_by_name && (
                <AuditLine entry={dayList[0]} testid={`cal-audit-${dateStr}`} compact />
              )}
              {worked && totalHours >= 5 && <div className="text-[10px] text-zinc-500 mt-1">+16 CHF</div>}
              {worked && totalHours > 0 && totalHours < 5 && <div className="text-[10px] text-zinc-400 italic mt-1" title="Diurnă acordată doar dacă lucrezi ≥ 5h/zi">fără diurnă (&lt;5h)</div>}
              {canEdit && worked && dayList.length <= 1 && (
                <button
                  type="button"
                  onClick={() => setEditDayDialog({ date: dateStr, entries: dayList })}
                  className="mt-1 w-full text-[9px] font-semibold bg-zinc-100 hover:bg-amber-100 text-zinc-600 hover:text-amber-800 border border-zinc-200 hover:border-amber-300 rounded px-1 py-0.5 transition-colors flex items-center justify-center gap-1"
                  data-testid={`edit-day-adv-${dateStr}`}
                  title="Editează detaliat / adaugă alte alocări / șterge"
                >
                  <Pencil size={9}/> Edit avansat
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Zile lucrate detaliat + rapoarte atașate pe lună */}
      <PersonMonthWorkLog personId={personId} month={month} entries={entries} />

      {/* ACONT — avansuri / reținări / bonusuri */}
      <div className="mt-8 border-t border-[var(--border)] pt-6" data-testid="acont-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Wallet size={18} className="text-[#7C3AED]"/>
            <h3 className="font-display text-lg font-bold">ACONT</h3>
            <span className="text-[11px] text-zinc-500">avansuri · reținări · bonusuri (se calculează în salariu)</span>
          </div>
          {isAdmin && (
            <Button
              size="sm"
              onClick={() => setAcontDialog({ date: `${month}-${String(today.getDate()).padStart(2, "0")}`, source: acontSources[0] || "Avans cash", source_other: "", amount: "", note: "" })}
              className="bg-[#7C3AED] hover:bg-[#6D28D9]"
              data-testid="add-acont-btn"
            >
              <Plus size={14} className="mr-1"/> Adaugă ACONT
            </Button>
          )}
        </div>
        {aconturi.length === 0 ? (
          <div className="text-sm text-zinc-400 italic px-2">Nicio intrare ACONT pentru luna selectată.</div>
        ) : (
          <div className="space-y-1.5">
            {aconturi.map((a) => {
              const positive = (a.amount || 0) >= 0;
              const srcLabel = a.source === "Altă sursă" ? (a.source_other || "Altă sursă") : a.source;
              return (
                <div key={a.id} className="flex items-center gap-3 px-3 py-2 rounded-md bg-zinc-50 border border-zinc-200" data-testid={`acont-row-${a.id}`}>
                  <div className="text-xs text-zinc-500 font-mono w-20 shrink-0">{fmtRoDate(a.date)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{srcLabel}</div>
                    {a.note && <div className="text-[11px] text-zinc-500 truncate">{a.note}</div>}
                  </div>
                  <div className={`font-display font-bold text-sm ${positive ? "text-green-600" : "text-red-600"}`}>
                    {positive ? "+" : ""}{Number(a.amount).toFixed(2)} CHF
                  </div>
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => setAcontDialog({
                          id: a.id,
                          date: a.date,
                          source: a.source || (acontSources[0] || "Avans cash"),
                          source_other: a.source_other || "",
                          amount: String(a.amount ?? ""),
                          note: a.note || "",
                        })}
                        className="text-zinc-400 hover:text-[#7C3AED] transition-colors"
                        title="Editează"
                        data-testid={`edit-acont-${a.id}`}
                      >
                        <Pencil size={14}/>
                      </button>
                      <button
                        onClick={() => removeAcont(a.id)}
                        className="text-zinc-400 hover:text-red-600 transition-colors"
                        title="Șterge"
                        data-testid={`del-acont-${a.id}`}
                      >
                        <Trash2 size={14}/>
                      </button>
                    </>
                  )}
                </div>
              );
            })}
            <div className="flex justify-end pt-2">
              <div className="text-sm">
                <span className="text-zinc-500">Total ACONT: </span>
                <strong className={acontTotal < 0 ? "text-red-600" : "text-green-600"} data-testid="acont-total">
                  {acontTotal >= 0 ? "+" : ""}{acontTotal.toFixed(2)} CHF
                </strong>
              </div>
            </div>
          </div>
        )}
      </div>

      {acontDialog && (
        <AcontDialog
          form={acontDialog}
          sources={acontSources}
          personId={personId}
          onClose={() => setAcontDialog(null)}
          onSaved={(doc) => {
            setAconturi((x) => {
              const exists = x.find((it) => it.id === doc.id);
              const next = exists
                ? x.map((it) => (it.id === doc.id ? doc : it))
                : [...x, doc];
              return next.sort((a, b) => a.date.localeCompare(b.date));
            });
            setAcontDialog(null);
          }}
        />
      )}
      {tagesraportDialog && (
        <QuickTagesraportDialog
          info={tagesraportDialog}
          onClose={() => setTagesraportDialog(null)}
          onSaved={async () => {
            setTagesraportDialog(null);
            // Re-fetch missing reports map ca să dispară warning-ul
            try {
              const { data } = await api.get(`/persons/${personId}/calendar/tagesraport-status?month=${month}`);
              setMissingReports(data || {});
            } catch {}
            toast.success("Tagesraport adăugat — warning eliminat");
          }}
        />
      )}
      {editDayDialog && (
        <EditDayDialog
          personId={personId}
          personName={personName}
          date={editDayDialog.date}
          initialEntries={editDayDialog.entries}
          defaultRate={defaultRate}
          allProjects={allProjects}
          allocByDate={allocByDate[editDayDialog.date] || []}
          onClose={() => setEditDayDialog(null)}
          onSaved={async () => {
            setEditDayDialog(null);
            await load();
            toast.success("Ziua a fost actualizată");
          }}
        />
      )}
    </div>
  );
}

/**
 * EditDayDialog — editor complet pentru o zi din calendar (multi-entry).
 * Permite adăugarea/editarea/ștergerea individuală a fiecărei alocări (proiect / regie / magazie / birou).
 * Folosește PUT /persons/:pid/calendar/day care înlocuiește TOATĂ ziua cu lista trimisă.
 */
function EditDayDialog({ personId, personName, date, initialEntries, defaultRate, allProjects, allocByDate, onClose, onSaved }) {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState(() =>
    (initialEntries || []).map((e) => ({
      kind: e.kind || (e.regie_id ? "regie" : e.project_id ? "project" : "project"),
      project_id: e.project_id || "",
      regie_id: e.regie_id || "",
      label: e.project_label || "",
      hours: e.hours ?? 0,
      hours_driven: e.hours_driven ?? 0,
      color: e.color || "",
      note: e.note || "",
    }))
  );
  const [regii, setRegii] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/regii");
        setRegii(Array.isArray(data) ? data : []);
      } catch { setRegii([]); }
    })();
  }, []);

  const setItem = (idx, patch) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const addItem = () => setItems((arr) => [...arr, { kind: "project", project_id: "", regie_id: "", label: "", hours: 0, hours_driven: 0, color: "", note: "" }]);

  const save = async () => {
    // Validare: fiecare item cu ore > 0 trebuie să aibă un target (proj/regie/magazie/birou)
    // BYPASS admin: adminul poate salva cu proiect/regie null (pentru situații excepționale).
    if (!isAdmin) {
      for (const it of items) {
        const h = parseFloat(it.hours) || 0;
        const hd = parseFloat(it.hours_driven) || 0;
        if (h <= 0 && hd <= 0) continue;
        if (it.kind === "project" && !it.project_id) {
          toast.error("O alocare de proiect necesită selectarea proiectului");
          return;
        }
        if (it.kind === "regie" && !it.regie_id) {
          toast.error("O alocare de regie necesită selectarea regiei");
          return;
        }
      }
    }
    setSaving(true);
    try {
      await api.put(`/persons/${personId}/calendar/day`, {
        date,
        hourly_rate: parseFloat(defaultRate) || 0,
        entries: items
          .filter((it) => (parseFloat(it.hours) || 0) > 0 || (parseFloat(it.hours_driven) || 0) > 0)
          .map((it) => ({
            kind: it.kind,
            project_id: it.kind === "project" ? (it.project_id || null) : null,
            regie_id: it.kind === "regie" ? (it.regie_id || null) : null,
            label: it.label || null,
            hours: parseFloat(it.hours) || 0,
            hours_driven: parseFloat(it.hours_driven) || 0,
            color: it.color || null,
            note: it.note || null,
          })),
      });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setSaving(false); }
  };

  const deleteAll = async () => {
    // Ștergere completă = trimit lista goală → backend delete_many
    if (!window.confirm(`Ștergi TOATE alocările pentru ${personName} pe ${date}?`)) return;
    setSaving(true);
    try {
      await api.put(`/persons/${personId}/calendar/day`, {
        date,
        hourly_rate: parseFloat(defaultRate) || 0,
        entries: [],
      });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    } finally { setSaving(false); }
  };

  // Sortare proiecte: cele alocate în ziua respectivă apar primele.
  // Filtrare: doar proiectele/regiile ACTIVE pe această zi (+ cele deja selectate în items).
  const allocIds = new Set((allocByDate || []).filter((a) => !a.is_regie).map((a) => a.id));
  const selectedProjIds = new Set(items.map((it) => it.project_id).filter(Boolean));
  const selectedRegieIds = new Set(items.map((it) => it.regie_id).filter(Boolean));
  const projectOptions = allProjects
    .filter((p) => selectedProjIds.has(p.id) || projectActiveOnDay(p, date))
    .sort((a, b) => {
      const aa = allocIds.has(a.id) ? 0 : 1;
      const bb = allocIds.has(b.id) ? 0 : 1;
      if (aa !== bb) return aa - bb;
      return (a.name || "").localeCompare(b.name || "", "ro");
    });
  const regiiSorted = regii
    .filter((r) => selectedRegieIds.has(r.id) || regieActiveOnDay(r, date))
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ro"));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="edit-day-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil size={18} className="text-amber-600"/>
            Editează ziua — {personName}
          </DialogTitle>
          <DialogDescription>
            Data: <strong>{date}</strong> · Modifică orele, proiectul/regia sau șterge alocări individuale.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {items.length === 0 && (
            <div className="text-sm text-zinc-500 italic px-2 py-3 border border-dashed border-zinc-300 rounded">
              Nicio alocare. Adaugă una nouă mai jos ↓
            </div>
          )}
          {items.map((it, idx) => (
            <div key={idx} className="border border-zinc-200 rounded-lg p-3 bg-zinc-50" data-testid={`edit-day-row-${idx}`}>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <Label className="small-label">Tip</Label>
                  <Select value={it.kind} onValueChange={(v) => setItem(idx, { kind: v, project_id: v === "project" ? it.project_id : "", regie_id: v === "regie" ? it.regie_id : "" })}>
                    <SelectTrigger className="mt-1 h-9" data-testid={`edit-day-kind-${idx}`}><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="project">Proiect</SelectItem>
                      <SelectItem value="regie">Regie</SelectItem>
                      <SelectItem value="magazie">🏪 Magazie</SelectItem>
                      <SelectItem value="birou">🏢 Birou</SelectItem>
                      <SelectItem value="dismantle">Demontaj</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-5">
                  {it.kind === "project" || it.kind === "dismantle" ? (
                    <>
                      <Label className="small-label">Proiect</Label>
                      <Select value={it.project_id || "__none__"} onValueChange={(v) => setItem(idx, { project_id: v === "__none__" ? "" : v })}>
                        <SelectTrigger className="mt-1 h-9" data-testid={`edit-day-project-${idx}`}><SelectValue placeholder="— Alege —"/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Niciunul —</SelectItem>
                          {projectOptions.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{allocIds.has(p.id) ? "⭐ " : ""}{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  ) : it.kind === "regie" ? (
                    <>
                      <Label className="small-label">Regie</Label>
                      <Select value={it.regie_id || "__none__"} onValueChange={(v) => setItem(idx, { regie_id: v === "__none__" ? "" : v })}>
                        <SelectTrigger className="mt-1 h-9" data-testid={`edit-day-regie-${idx}`}><SelectValue placeholder="— Alege —"/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Niciuna —</SelectItem>
                          {regiiSorted.map((r) => (
                            <SelectItem key={r.id} value={r.id}>{r.name}{r.firma ? ` · ${r.firma}` : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  ) : (
                    <div className="text-xs text-zinc-500 italic mt-6 px-2">
                      {it.kind === "magazie" ? "🏪 Activitate la Magazie" : "🏢 Activitate la Birou"}
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <Label className="small-label">Ore</Label>
                  <Input type="number" step="0.5" min="0" max="24" value={it.hours} onChange={(e) => setItem(idx, { hours: e.target.value })} className="mt-1 h-9" data-testid={`edit-day-hours-${idx}`}/>
                </div>
                <div className="col-span-2">
                  <Label className="small-label">🚗 Conduse</Label>
                  <Input type="number" step="0.5" min="0" max="24" value={it.hours_driven} onChange={(e) => setItem(idx, { hours_driven: e.target.value })} className="mt-1 h-9" data-testid={`edit-day-driven-${idx}`}/>
                </div>
              </div>
              <div className="flex items-center justify-end mt-2">
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  className="text-red-600 hover:bg-red-50 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1"
                  data-testid={`edit-day-delete-${idx}`}
                >
                  <Trash2 size={12}/> Șterge alocarea
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addItem}
            className="w-full py-2 border-2 border-dashed border-zinc-300 hover:border-amber-400 hover:bg-amber-50 text-zinc-600 hover:text-amber-700 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
            data-testid="edit-day-add"
          >
            <Plus size={14}/> Adaugă alocare nouă
          </button>
        </div>

        <DialogFooter className="gap-2 mt-2">
          {items.length > 0 && (
            <Button variant="outline" onClick={deleteAll} disabled={saving} className="text-red-600 hover:bg-red-50 border-red-200" data-testid="edit-day-delete-all">
              Șterge toată ziua
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="edit-day-cancel">Anulează</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="edit-day-save">
            {saving ? "Se salvează…" : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function QuickTagesraportDialog({ info, onClose, onSaved }) {
  const [task, setTask] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState("60");
  const [category, setCategory] = useState("administrativ");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!task.trim() || task.trim().length < 3) {
      toast.error("Task-ul trebuie să aibă minim 3 caractere");
      return;
    }
    const dur = parseInt(duration);
    if (!dur || dur < 1) {
      toast.error("Durata trebuie > 0 minute");
      return;
    }
    setSaving(true);
    try {
      await api.post("/tagesraporte", {
        person_id: info.person_id,
        date: info.date,
        task: task.trim(),
        description: description.trim() || null,
        duration_min: dur,
        category,
      });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="quick-tagesraport-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            📝 Tagesraport — {info.person_name}
          </DialogTitle>
          <DialogDescription>
            Pentru ziua <strong>{info.date}</strong> (zi cu activitate la magazie)
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Activitate (task) *</Label>
            <Input value={task} onChange={(e) => setTask(e.target.value)} placeholder="ex: Pregătire materiale, inventar, etc." className="mt-1" data-testid="tagesraport-task" />
          </div>
          <div>
            <Label>Descriere (opțional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Detalii…" className="mt-1" rows={2} data-testid="tagesraport-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Durată (min) *</Label>
              <Input type="number" step="15" min="1" value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1" data-testid="tagesraport-duration" />
            </div>
            <div>
              <Label>Categorie</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1" data-testid="tagesraport-category"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="administrativ">Administrativ</SelectItem>
                  <SelectItem value="comercial">Comercial</SelectItem>
                  <SelectItem value="santier">Șantier</SelectItem>
                  <SelectItem value="it">IT</SelectItem>
                  <SelectItem value="altele">Altele</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="tagesraport-cancel">Anulează</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tagesraport-save">
            {saving ? "Se salvează…" : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AcontDialog({ form: initial, sources, personId, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const isEdit = Boolean(initial?.id);
  const submit = async () => {
    if (!form.date) { toast.error("Data este obligatorie"); return; }
    if (!form.source) { toast.error("Sursa este obligatorie"); return; }
    if (form.source === "Altă sursă" && !form.source_other?.trim()) {
      toast.error("Specifică sursa pentru 'Altă sursă'");
      return;
    }
    const amount = parseFloat(form.amount);
    if ((!amount && amount !== 0) || isNaN(amount)) { toast.error("Sumă invalidă"); return; }
    try {
      const payload = {
        date: form.date,
        source: form.source,
        source_other: form.source === "Altă sursă" ? form.source_other.trim() : null,
        amount,
        note: form.note || null,
      };
      const { data } = isEdit
        ? await api.put(`/persons/${personId}/aconturi/${form.id}`, payload)
        : await api.post(`/persons/${personId}/aconturi`, payload);
      toast.success(isEdit ? "ACONT actualizat" : "ACONT adăugat");
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="acont-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Editează intrare ACONT" : "Adaugă intrare ACONT"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="small-label">Dată</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" data-testid="acont-date"/>
          </div>
          <div>
            <Label className="small-label">Sursă</Label>
            <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
              <SelectTrigger className="mt-1.5" data-testid="acont-source"><SelectValue/></SelectTrigger>
              <SelectContent>
                {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.source === "Altă sursă" && (
            <div>
              <Label className="small-label">Specifică sursa</Label>
              <Input value={form.source_other} onChange={(e) => setForm({ ...form, source_other: e.target.value })} className="mt-1.5" placeholder="ex: cazare avans" data-testid="acont-source-other"/>
            </div>
          )}
          <div>
            <Label className="small-label">Sumă (CHF) — pozitiv = adaugă, negativ = reține</Label>
            <Input
              type="number" step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="mt-1.5"
              placeholder="ex: 200 sau -50"
              data-testid="acont-amount"
            />
          </div>
          <div>
            <Label className="small-label">Notă (opțional)</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} className="bg-[#7C3AED] hover:bg-[#6D28D9]" data-testid="acont-submit">Salvează</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, v, color, testId }) {
  return (
    <div className="card-flat p-3" data-testid={testId}>
      <div className="small-label">{label}</div>
      <div className="font-display text-2xl font-extrabold mt-1" style={color ? { color } : {}}>{v}</div>
    </div>
  );
}


// ---------- User Account Pane ----------
const ACTIONS = ["view", "add", "edit", "delete"];
const ACTION_LABELS = { view: "Vizualizare", add: "Adăugare", edit: "Modificare", delete: "Ștergere" };

const slugify = (s) => (s || "")
  .toLowerCase()
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "")
  .replace(/^-+|-+$/g, "");

const PASSWORD_RULES = [
  { code: "min_len", label: "Minim 8 caractere", test: (p) => p.length >= 8 },
  { code: "uppercase", label: "Cel puțin o literă mare (A-Z)", test: (p) => /[A-Z]/.test(p) },
  { code: "lowercase", label: "Cel puțin o literă mică (a-z)", test: (p) => /[a-z]/.test(p) },
  { code: "digit", label: "Cel puțin o cifră (0-9)", test: (p) => /\d/.test(p) },
];

function UserAccountPane({ person }) {
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", name: "", role: "user", permissions: {} });
  const [resetPw, setResetPw] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [u, m] = await Promise.all([api.get("/users"), api.get("/modules")]);
      setUsers(u.data || []);
      setModules((m.data || []).filter((x) => x.code !== "DASHBOARD"));
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [person.id]);

  const linkedUser = users.find((u) => u.person_id === person.id);

  const startCreate = () => {
    const suggested = `${slugify(person.prenume)}.${slugify(person.nume)}`.replace(/^\.+|\.+$/g, "") || `user${Date.now() % 10000}`;
    setForm({
      username: suggested,
      password: "",
      name: `${person.nume || ""} ${person.prenume || ""}`.trim() || suggested,
      role: "user",
      permissions: {},
    });
    setCreating(true);
  };

  const togglePerm = (code, action) => {
    setForm((f) => ({
      ...f,
      permissions: {
        ...f.permissions,
        [code]: { ...(f.permissions[code] || {}), [action]: !(f.permissions[code] || {})[action] },
      },
    }));
  };

  const setRowAll = (code, value) => {
    setForm((f) => ({
      ...f,
      permissions: {
        ...f.permissions,
        [code]: { view: value, add: value, edit: value, delete: value },
      },
    }));
  };

  const submit = async () => {
    const u = form.username.trim();
    if (!u || !/^[a-zA-Z0-9._-]{3,}$/.test(u)) {
      toast.error("Username invalid (min 3 caractere, doar litere/cifre/. _ -)");
      return;
    }
    const failed = PASSWORD_RULES.filter((r) => !r.test(form.password));
    if (failed.length > 0) {
      toast.error(`Parola nu respectă: ${failed.map((r) => r.label).join(", ")}`);
      return;
    }
    try {
      await api.post("/users", { ...form, username: u, person_id: person.id });
      toast.success(`Cont creat pentru ${u}`);
      setCreating(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la creare"); }
  };

  const togglePermExisting = async (code, action) => {
    if (!linkedUser) return;
    const next = {
      ...(linkedUser.permissions || {}),
      [code]: { ...((linkedUser.permissions || {})[code] || {}), [action]: !((linkedUser.permissions || {})[code] || {})[action] },
    };
    try {
      await api.put(`/users/${linkedUser.id}`, { permissions: next });
      toast.success("Drepturi actualizate");
      load();
    } catch { toast.error("Eroare la salvare"); }
  };

  const setRowAllExisting = async (code, value) => {
    if (!linkedUser) return;
    const next = {
      ...(linkedUser.permissions || {}),
      [code]: { view: value, add: value, edit: value, delete: value },
    };
    try {
      await api.put(`/users/${linkedUser.id}`, { permissions: next });
      load();
    } catch {}
  };

  const toggleActive = async () => {
    if (!linkedUser) return;
    try {
      await api.put(`/users/${linkedUser.id}`, { active: !linkedUser.active });
      toast.success(linkedUser.active ? "Cont dezactivat" : "Cont reactivat");
      load();
    } catch { toast.error("Eroare"); }
  };

  const resetPassword = async () => {
    if (!linkedUser) return;
    const failed = PASSWORD_RULES.filter((r) => !r.test(resetPw));
    if (failed.length > 0) { toast.error(`Parola nu respectă: ${failed.map((r) => r.label).join(", ")}`); return; }
    try {
      await api.put(`/users/${linkedUser.id}`, { password: resetPw });
      toast.success("Parola a fost resetată");
      setResetPw("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  if (loading) return <div className="card-flat p-6 text-sm text-zinc-400 italic">Se încarcă…</div>;

  if (!linkedUser && !creating) {
    return (
      <div className="card-flat p-6 text-center" data-testid="no-account-state">
        <div className="text-zinc-500 text-sm mb-4">Această persoană nu are cont de utilizator în platformă.</div>
        <Button onClick={startCreate} className="bg-[var(--primary)]" data-testid="btn-create-account"><UserRound size={14} className="mr-2"/>Creează cont</Button>
      </div>
    );
  }

  if (creating) {
    const passed = PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(form.password) }));
    const score = passed.filter((r) => r.ok).length;
    return (
      <div className="card-flat p-6 space-y-4" data-testid="create-account-form">
        <div className="font-display text-lg font-bold">Creare cont · {person.nume} {person.prenume}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="small-label">Username</Label>
            <Input className="mt-1 font-mono" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="create-username" />
            <p className="text-[11px] text-zinc-500 mt-1">Auto-sugerat din prenume.nume — poți modifica.</p>
          </div>
          <div>
            <Label className="small-label">Nume afișat</Label>
            <Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Label className="small-label">Rol</Label>
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v, permissions: {} }))}>
              <SelectTrigger className="mt-1" data-testid="create-role"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">👷 Muncitor (Angajat) — acces redus</SelectItem>
                <SelectItem value="magazioner">🔧 Magazioner / Mecanic — magazie + flotă</SelectItem>
                <SelectItem value="planificator">📋 Planificator — proiecte, plan, persoane</SelectItem>
                <SelectItem value="admin">👑 Administrator — acces complet</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-zinc-500 mt-1">Permisiunile inițiale se aplică automat din template-ul rolului. Le poți ajusta mai jos.</p>
          </div>
          <div className="md:col-span-2">
            <Label className="small-label">Parolă inițială</Label>
            <PasswordInput className="mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="create-password" />
            {form.password && (
              <ul className="text-[11px] mt-1.5 space-y-0.5">
                {passed.map((r) => (
                  <li key={r.code} className={r.ok ? "text-[var(--green-valid)]" : "text-zinc-400"}>{r.ok ? "✓" : "○"} {r.label}</li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          <div className="small-label mb-2">Drepturi inițiale per modul</div>
          <PermissionsMatrix modules={modules} permissions={form.permissions} onToggle={togglePerm} onRowAll={setRowAll} />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={() => setCreating(false)}>Anulează</Button>
          <Button onClick={submit} disabled={score < 4} className="bg-[var(--primary)]" data-testid="submit-create-account">Creează cont</Button>
        </div>
      </div>
    );
  }

  // existing user
  return (
    <div className="space-y-4" data-testid="account-existing">
      <div className="card-flat p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="font-display text-lg font-bold">Cont: <span className="font-mono text-[var(--primary)]">{linkedUser.username}</span></div>
            <div className="text-xs text-zinc-500 mt-0.5">Creat: {fmtRoDate(linkedUser.created_at)}</div>
            <div className="mt-2 flex items-center gap-2">
              <Label className="small-label whitespace-nowrap">Rol:</Label>
              <Select
                value={linkedUser.role}
                onValueChange={async (newRole) => {
                  if (newRole === linkedUser.role) return;
                  if (!(await confirm({ body: `Schimbi rolul din "${linkedUser.role}" în "${newRole}"? Permisiunile vor fi resetate la template-ul noului rol.`, danger: true }))) return;
                  try {
                    await api.put(`/users/${linkedUser.id}`, { role: newRole, reset_permissions_to_role: true });
                    toast.success(`Rol schimbat în ${newRole}`);
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
                }}
              >
                <SelectTrigger className="h-8 w-56" data-testid="existing-role-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">👷 Muncitor (Angajat)</SelectItem>
                  <SelectItem value="magazioner">🔧 Magazioner / Mecanic</SelectItem>
                  <SelectItem value="planificator">📋 Planificator</SelectItem>
                  <SelectItem value="admin">👑 Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${linkedUser.active === false ? "bg-zinc-100 text-zinc-600" : "bg-[var(--green-bg)] text-[var(--green-valid)]"}`}>
              {linkedUser.active === false ? "Dezactivat" : "Activ"}
            </span>
            <Button variant="outline" size="sm" onClick={toggleActive}>{linkedUser.active === false ? "Reactivează" : "Dezactivează"}</Button>
          </div>
        </div>
      </div>

      <div className="card-flat p-5">
        <div className="font-display text-base font-bold mb-3">Drepturi per modul</div>
        <PermissionsMatrix modules={modules} permissions={linkedUser.permissions || {}} onToggle={togglePermExisting} onRowAll={setRowAllExisting} />
      </div>

      <div className="card-flat p-5">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="font-display text-base font-bold">🔑 Parola actuală</div>
          <div className="flex items-center gap-2">
            <CopyCredentialsButton username={linkedUser.username} userId={linkedUser.id} personName={`${person.nume || ""} ${person.prenume || ""}`.trim()} />
            <ViewPasswordButton userId={linkedUser.id} />
          </div>
        </div>
        <div className="text-[11px] text-zinc-500">
          Apasă „Afișează" pentru a vedea parola curentă a contului. „Copiază credențiale" pune în clipboard <code>username / parolă</code> gata de trimis pe WhatsApp. Vizibil doar pentru administratori și loggat în jurnalul de audit.
        </div>
      </div>

      <div className="card-flat p-5">
        <div className="font-display text-base font-bold mb-2">Resetare parolă</div>
        <div className="flex items-center gap-2">
          <PasswordInput className="flex-1 font-mono" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Parolă nouă" data-testid="reset-password-input" />
          <Button onClick={resetPassword} disabled={!resetPw} className="bg-[var(--primary)]" data-testid="reset-password-btn">Resetează</Button>
        </div>
        {resetPw && (
          <ul className="text-[11px] mt-2 space-y-0.5">
            {PASSWORD_RULES.map((r) => (
              <li key={r.code} className={r.test(resetPw) ? "text-[var(--green-valid)]" : "text-zinc-400"}>{r.test(resetPw) ? "✓" : "○"} {r.label}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ViewPasswordButton({ userId }) {
  const [visible, setVisible] = useState(false);
  const [password, setPassword] = useState(null);
  const [hasPassword, setHasPassword] = useState(true);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchPassword = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${userId}/password`);
      setPassword(data.password || "");
      setHasPassword(!!data.has_password);
      setVisible(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcarea parolei");
    } finally {
      setLoading(false);
    }
  };

  const hide = () => {
    setVisible(false);
    setPassword(null);
    setCopied(false);
  };

  const copy = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      toast.success("Parola copiată în clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Nu am putut copia");
    }
  };

  if (!visible) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={fetchPassword}
        disabled={loading}
        data-testid="view-password-btn"
      >
        {loading ? "Se încarcă..." : "👁 Afișează"}
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {hasPassword ? (
        <code
          className="font-mono text-sm bg-amber-50 border border-amber-300 px-3 py-1 rounded text-amber-900 select-all"
          data-testid="view-password-value"
          title="Parola curentă"
        >
          {password}
        </code>
      ) : (
        <span className="text-xs text-zinc-500 italic" data-testid="view-password-empty">
          Necunoscută — parola a fost setată înainte de activarea funcției. Resetează parola pentru a o face vizibilă.
        </span>
      )}
      {hasPassword && (
        <Button
          size="sm"
          variant="outline"
          onClick={copy}
          data-testid="view-password-copy"
        >
          {copied ? "✓ Copiat" : "📋 Copiază"}
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={hide} data-testid="view-password-hide">
        🙈 Ascunde
      </Button>
    </div>
  );
}



function CopyCredentialsButton({ username, userId, personName }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const handle = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/users/${userId}/password`);
      if (!data.has_password || !data.password) {
        toast.error("Parolă necunoscută. Resetează-o întâi din secțiunea de jos.");
        return;
      }
      const lines = [];
      if (personName) lines.push(`👤 ${personName}`);
      lines.push(`Cont ConVic:`);
      lines.push(`Utilizator: ${username}`);
      lines.push(`Parolă: ${data.password}`);
      lines.push(`Link: https://convic.net`);
      const text = lines.join("\n");
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Credențialele au fost copiate în clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la copiere");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={handle}
      disabled={busy}
      data-testid="copy-credentials-btn"
      title="Copiază username + parolă în clipboard (gata de trimis pe WhatsApp)"
    >
      {copied ? "✓ Copiat" : busy ? "..." : "📋 Copiază credențiale"}
    </Button>
  );
}



function PermissionsMatrix({ modules, permissions, onToggle, onRowAll }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-[var(--border)]">
          <tr>
            <th className="p-2 text-left text-xs font-semibold text-zinc-500 uppercase">Modul</th>
            {ACTIONS.map((a) => (
              <th key={a} className="p-2 text-center text-xs font-semibold text-zinc-500 uppercase">{ACTION_LABELS[a]}</th>
            ))}
            <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">Acțiuni</th>
          </tr>
        </thead>
        <tbody>
          {modules.map((m) => {
            const row = permissions[m.code] || {};
            const allOn = ACTIONS.every((a) => row[a]);
            return (
              <tr key={m.code} className="border-b border-[var(--border)] last:border-b-0">
                <td className="p-2 font-medium">{m.name}</td>
                {ACTIONS.map((a) => (
                  <td key={a} className="p-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!row[a]}
                      onChange={() => onToggle(m.code, a)}
                      data-testid={`perm-${m.code}-${a}`}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </td>
                ))}
                <td className="p-2 text-right">
                  <button
                    onClick={() => onRowAll(m.code, !allOn)}
                    className="text-[11px] text-[var(--primary)] hover:underline"
                  >
                    {allOn ? "Niciunul" : "Toate"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------- My Expenses Pane (Cheltuielile mele) ----------
function MyExpensesPane({ personId, isAdmin }) {
  const confirm = useConfirm();
  const [entries, setEntries] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ amount: "", date: new Date().toISOString().slice(0, 10), reason: "", file_ids: [] });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get("/salary-subcategories"),
        api.get(`/salary-entries?person_id=${personId}&subcategory_code=CHELTUIELI`),
      ]);
      setSubs(s.data || []);
      setEntries(e.data || []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [personId]);

  const expenseSub = subs.find((s) => s.code === "CHELTUIELI");

  const onUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: f } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, file_ids: [...prev.file_ids, f.id] }));
      toast.success("Fișier încărcat");
    } catch { toast.error("Eroare la upload"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error("Introdu o sumă pozitivă"); return; }
    try {
      await api.post("/salary-entries", {
        person_id: personId,
        subcategory_code: "CHELTUIELI",
        amount: parseFloat(form.amount),
        date: form.date,
        reason: form.reason,
        file_ids: form.file_ids,
      });
      toast.success(isAdmin ? "Cheltuială adăugată (auto-aprobată)" : "Cheltuială trimisă spre aprobare");
      setForm({ amount: "", date: new Date().toISOString().slice(0, 10), reason: "", file_ids: [] });
      setCreating(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const del = async (eid) => {
    if (!(await confirm({ body: "Ștergi această cheltuială?", danger: true }))) return;
    try { await api.delete(`/salary-entries/${eid}`); toast.success("Ștearsă"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  if (loading) return <div className="card-flat p-6 text-sm text-zinc-400 italic">Se încarcă…</div>;

  const totalApproved = entries.filter((e) => e.status === "approved").reduce((acc, e) => acc + (parseFloat(e.amount) || 0), 0);
  const totalPending = entries.filter((e) => e.status === "pending").reduce((acc, e) => acc + (parseFloat(e.amount) || 0), 0);

  return (
    <div className="space-y-3" data-testid="my-expenses-pane">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card-flat p-4 text-center">
          <div className="small-label">Aprobate</div>
          <div className="font-display text-2xl font-extrabold mt-1 text-[var(--green-valid)]">{totalApproved.toFixed(2)} CHF</div>
        </div>
        <div className="card-flat p-4 text-center">
          <div className="small-label">În așteptare</div>
          <div className="font-display text-2xl font-extrabold mt-1 text-amber-600">{totalPending.toFixed(2)} CHF</div>
        </div>
        <div className="card-flat p-4 text-center">
          <div className="small-label">Total înregistrări</div>
          <div className="font-display text-2xl font-extrabold mt-1 text-zinc-700">{entries.length}</div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => setCreating(!creating)} className="bg-[var(--primary)]" data-testid="btn-add-expense">
          {creating ? "Anulează" : "+ Adaugă cheltuială"}
        </Button>
      </div>

      {creating && (
        <div className="card-flat p-5 space-y-3" data-testid="expense-form">
          <h3 className="font-display text-base font-bold">Cheltuială nouă</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Suma (CHF)</Label><Input type="number" min={0.01} step={0.01} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount"/></div>
            <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}/></div>
          </div>
          <div>
            <Label>Descriere / motiv</Label>
            <Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="ex: combustibil, materiale, masă"/>
          </div>
          <div>
            <Label>Atașează poze cu bonul / dovadă (max recomandat: 5 imagini)</Label>
            <label className="mt-1 flex items-center gap-2 px-3 py-2 border border-dashed border-[var(--border)] rounded cursor-pointer hover:border-[var(--primary)] text-sm text-zinc-600">
              <span>{uploading ? "Se încarcă..." : "📎 Încarcă imagine"}</span>
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} data-testid="expense-file-input"/>
            </label>
            {form.file_ids.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {form.file_ids.map((fid) => (
                  <div key={fid} className="text-xs px-2 py-1 rounded bg-zinc-100 flex items-center gap-1">
                    📎 {fid.slice(0, 8)}…
                    <button onClick={() => setForm({ ...form, file_ids: form.file_ids.filter((x) => x !== fid) })} className="text-red-600 ml-1">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setCreating(false)}>Anulează</Button>
            <Button onClick={submit} className="bg-[var(--primary)]" data-testid="expense-submit">{isAdmin ? "Adaugă" : "Trimite spre aprobare"}</Button>
          </div>
          {!isAdmin && (
            <p className="text-[11px] text-zinc-500 italic">După trimitere, cheltuiala va apărea cu status „În așteptare". Adminul o va aproba sau respinge.</p>
          )}
        </div>
      )}

      <div className="card-flat p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-[var(--border)]">
            <tr>
              <th className="p-2 text-left text-xs font-semibold text-zinc-500 uppercase">Data</th>
              <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">Sumă</th>
              <th className="p-2 text-left text-xs font-semibold text-zinc-500 uppercase">Motiv</th>
              <th className="p-2 text-center text-xs font-semibold text-zinc-500 uppercase">Status</th>
              <th className="p-2 text-center text-xs font-semibold text-zinc-500 uppercase">Atașamente</th>
              <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={6} className="p-4 text-center text-zinc-400 italic">Nicio cheltuială înregistrată.</td></tr>
            )}
            {entries.map((e) => {
              const styles = e.status === "approved" ? "bg-[var(--green-bg)] text-[var(--green-valid)]"
                : e.status === "pending" ? "bg-amber-100 text-amber-700"
                : "bg-zinc-100 text-zinc-500";
              const label = e.status === "approved" ? "Aprobat" : e.status === "pending" ? "În așteptare" : "Respins";
              return (
                <tr key={e.id} className="border-b border-[var(--border)] last:border-b-0" data-testid={`my-expense-${e.id}`}>
                  <td className="p-2 tabular-nums">{fmtRoDate(e.date)}</td>
                  <td className="p-2 text-right tabular-nums font-bold">{(parseFloat(e.amount) || 0).toFixed(2)}</td>
                  <td className="p-2 text-zinc-600 max-w-[260px] truncate" title={e.reason || ""}>{e.reason || "—"}</td>
                  <td className="p-2 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles}`}>{label}</span>
                    {e.status === "rejected" && e.reject_reason && <div className="text-[10px] text-zinc-500 mt-0.5">{e.reject_reason}</div>}
                  </td>
                  <td className="p-2 text-center">
                    {e.file_ids?.length > 0 ? <span className="text-xs">{e.file_ids.length} 📎</span> : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="p-2 text-right">
                    {(isAdmin || e.status === "pending") && (
                      <button onClick={() => del(e.id)} className="text-red-600 p-1 hover:bg-red-50 rounded" title="Șterge"><Trash2 size={14}/></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}



function PersonAssignedKeysCard({ personId }) {
  const [keys, setKeys] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/persons/${personId}/keys`);
        if (!cancelled) setKeys(r.data || []);
      } catch {
        if (!cancelled) setKeys([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [personId]);

  if (loading) return null;
  if (keys.length === 0) {
    return (
      <div className="card-flat p-4 mt-4 bg-zinc-50 text-sm text-zinc-500 italic" data-testid="person-keys-empty">
        🔑 Nicio cheie alocată acestei persoane.
      </div>
    );
  }

  return (
    <div className="card-flat p-4 mt-4 border-l-4 border-l-amber-500 bg-amber-50/40" data-testid="person-keys-card">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">🔑</span>
        <h3 className="font-display text-sm font-bold uppercase tracking-wider text-amber-900">
          Chei deținute ({keys.length})
        </h3>
      </div>
      <div className="space-y-1.5">
        {keys.map((k) => (
          <div
            key={k.id}
            className="text-[13px] flex items-center gap-2 px-2 py-1.5 bg-white border border-amber-200 rounded"
            data-testid={`person-key-${k.id}`}
          >
            <span className="text-lg">{k.key_type_icon || "🔑"}</span>
            <span>
              <strong>{k.key_type_name}</strong> <span className="font-mono">#{k.number}</span>
              <span className="text-zinc-500"> · de la </span>
              <strong className="text-zinc-900">{k.rental_label}</strong>
            </span>
            {k.assigned_at && <span className="ml-auto text-[10px] text-zinc-500">de pe {fmtRoDate(k.assigned_at)}</span>}
          </div>
        ))}
      </div>
      <div className="text-[11px] text-zinc-500 mt-2 italic">
        Cheile sunt responsabilitatea persoanei. Gestionează din modulul <strong>Chirii</strong> → apartament → secțiunea „Chei".
      </div>
    </div>
  );
}

// ===== 📅 Plan — unde e planificată persoana în zilele următoare =====
function PersonPlanTab({ personId }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);
  useEffect(() => {
    setData(null);
    api.get(`/persons/${personId}/upcoming-plan`, { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setData({ days: [] }));
  }, [personId, days]);

  const DOW = ["Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă", "Duminică"];
  if (!data) return <div className="text-sm text-zinc-400 py-6 text-center">Se încarcă planul…</div>;
  const withEntries = (data.days || []).filter((d) => d.entries.length > 0);

  return (
    <div data-testid="person-plan-tab">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-zinc-500">Planul pe următoarele</span>
        {[7, 14, 30].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setDays(n)}
            className={`px-2.5 py-1 rounded-full text-xs font-bold border transition-colors ${days === n ? "bg-[var(--primary)] text-white border-[var(--primary)]" : "border-zinc-300 text-zinc-600 hover:border-zinc-500"}`}
            data-testid={`plan-days-${n}`}
          >
            {n} zile
          </button>
        ))}
      </div>
      {withEntries.length === 0 ? (
        <div className="text-sm text-zinc-400 border border-dashed border-zinc-200 rounded p-6 text-center" data-testid="plan-empty">
          Nicio alocare în planificator pentru următoarele {days} zile.
        </div>
      ) : (
        <div className="space-y-3">
          {withEntries.map((d) => (
            <div key={d.date} className={`card-flat p-3 border-l-4 ${d.weekday >= 5 ? "border-l-amber-400" : "border-l-[var(--primary)]"}`} data-testid={`plan-day-${d.date}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-display font-extrabold text-sm">{DOW[d.weekday]}</span>
                <span className="text-xs text-zinc-500 font-mono">{fmtRoDate(d.date)}</span>
                {d.weekday >= 5 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">weekend</span>}
              </div>
              <div className="space-y-2">
                {d.entries.map((e, i) => (
                  <div key={i} className="rounded-md bg-zinc-50 border border-zinc-100 p-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: e.color || "#94a3b8" }} />
                      <span className="font-bold text-sm">{e.label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${e.kind === "regie" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
                        {e.kind === "regie" ? "REGIE" : e.kind === "project" ? "ȘANTIER" : "ALTELE"}
                      </span>
                      {e.adresa && <span className="text-xs text-zinc-500">📍 {e.adresa}</span>}
                    </div>
                    {e.note && <div className="text-xs text-zinc-600 mt-1">📝 {e.note}</div>}
                    {(e.scope || []).length > 0 && (
                      <div className="mt-1.5 space-y-0.5" data-testid="plan-scope">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">
                          🔨 De lucrat{e.zones_count > 0 ? ` · ${e.zones_count} ${e.zones_count === 1 ? "zonă" : "zone"}` : ""}
                        </div>
                        {e.scope.map((si, j) => (
                          <div key={j} className="text-xs text-zinc-700 flex items-center gap-1.5 flex-wrap">
                            <span className="font-semibold">{si.room || "—"}</span>
                            <span className="text-zinc-400">·</span>
                            <span>{si.material || "—"}</span>
                            {si.qty && <span className="font-bold text-[var(--primary)]">{si.qty} {si.unit || "m²"}</span>}
                            {si.zone && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">{si.zone}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {e.colleagues.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap mt-1.5 text-xs">
                        <span className="text-zinc-400">👥 cu:</span>
                        {e.colleagues.map((c) => (
                          <span key={c.id} className="px-1.5 py-0.5 rounded bg-white border border-zinc-200 font-medium">
                            {c.color && <span className="inline-block w-2 h-2 rounded-full mr-1 align-middle" style={{ background: c.color }} />}
                            {c.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {e.cars.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap mt-1.5 text-xs">
                        <span className="text-zinc-400">🚗</span>
                        {e.cars.map((c) => (
                          <span key={c.id} className="px-1.5 py-0.5 rounded bg-white border border-zinc-200">
                            <strong>{[c.marca, c.model].filter(Boolean).join(" ")}</strong>
                            {c.culoare ? ` · ${c.culoare}` : ""}
                            {c.plate ? ` · ${c.plate}` : ""}
                            {c.is_person_driver
                              ? <span className="ml-1 text-emerald-700 font-bold">🔑 el conduce</span>
                              : c.driver_name ? <span className="ml-1 text-zinc-500">🔑 {c.driver_name}</span> : null}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
